import { useParams, useSearchParams, useNavigate, Navigate, Link } from "react-router-dom";
import { usePageContextReporter } from "@/lib/page-context";
import { useQueries, useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { BudgetChip } from "@/pages/customers/budget/chip";
import { quoteCategory } from "@/pages/customers/budget/model";
import { api } from "@/lib/api";
import { ChargeCardPanel } from "@/components/charge-card-panel";
import { EstimateReviewPanel } from "./estimate-review-panel";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DataTable, type Column } from "@/components/ui/data-table";
import { PageSpinner } from "@/components/ui/spinner";
import { statusColor, formatDate, formatDollars } from "@/lib/utils";
import { formatPaidDate } from "@/lib/paid-date";
import { PartOfBiggerCheckButton, ReceivedPaymentTrace } from "@/pages/money/payments/invoice-bits";
import { dateSortValue, moneySortValue } from "@/lib/table-sort";
import {
    EstimateOptionGroups,
    InvoiceMismatchBanner,
    type EstimateOptionView,
    type InvoiceMismatch,
} from "./option-groups";
import { useOptionRemoval } from "./option-removal";
import { stripHtml } from "@/lib/strip-html";
import {
    ArrowLeft,
    ArrowUpDown,
    FileText,
    DollarSign,
    Send,
    Receipt,
    Loader2,
    Pencil,
    Trash2,
    Check,
    X,
    Plus,
    Settings,
    Briefcase,
    ExternalLink,
    Printer,
    Cloud,
    AlertTriangle,
    CheckCircle2,
    CheckCircle,
    Link2,
    Undo2,
    Languages,
    PackagePlus,
    Banknote,
    RotateCcw,
    Ban,
    Wrench,
    ChevronDown,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { TaskCreateButton } from "@/components/task-create-dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog } from "@/components/ui/dialog";
import { Select } from "@/components/ui/select";
import { EstimateDepositPanel } from "@/components/estimate-deposit-panel";
import { LaborCrewStepper, laborOf, type LaborShape } from "@/components/labor-crew-stepper";
import { BulkDeleteBar, LineCheckbox, useBulkLineDelete } from "@/components/bulk-line-delete";
import { RateCardBadge, RepriceFromCardButton } from "@/components/reprice-from-card";
import { PricebookLinePicker } from "@/components/pricebook-line-picker";
import { TagPhotoPanel } from "@/components/tag-photo-panel";
import { AddDeviceToSiteModal, type CreatedAsset } from "@/components/add-device-to-site-modal";
import { DocumentPreviewDialog } from "@/components/document-preview-dialog";
import { SendCustomerDialog } from "@/components/send-customer-dialog";
import { CancelEstimateDialog } from "@/components/cancel-estimate-dialog";
import { ReopenEstimateDialog } from "@/components/reopen-estimate-dialog";
import { RecordApprovalDialog } from "@/components/record-approval-dialog";
import { VoidInvoiceDialog } from "@/components/void-invoice-dialog";
import { TaxTreatmentPanel } from "@/components/tax-treatment-panel";
import { DetailErrorState } from "@/components/detail-error-state";
import { buildRecordPaymentBody } from "./recordPaymentHelpers";
import { estimateDeviceScope } from "./estimateDeviceScope";
import { useLineReorder } from "./useLineReorder";
import { needsTidying, tidyLineOrder } from "./lineOrder";
import { toast } from "sonner";
import { confirmDialog } from "@/components/ui/app-dialogs";
import { useMemo, useState } from "react";
import { DeviceMenu, type DeviceMenuPick } from "@/components/device-menu";
import { RichLineText } from "@/components/rich-line-text";
import { OfficeOnlyAssemblyNote, type OfficeAssembly } from "@/components/office-only-assembly-note";
import { initialRichHtml, RichLineEditor } from "@/components/rich-line-editor";
import { GroupedLineList, type PiecePreview } from "./estimate-lines/grouped-line-list";
import { PipeChoiceStrip } from "./estimate-lines/pipe-choice-strip";
import { FeeAudiencePick, type FeeAudienceInfo } from "./estimate-lines/fee-audience-pick";
import { DeviceBox, LineEditorPanel, type LineEditorSave } from "./estimate-lines/line-editor";
import { SelectionBar, SendGuard } from "./estimate-lines/selection-bar";
import { LineOriginKey, LineOriginTag, lineOrigin, removesInOneTap } from "./estimate-lines/line-origin";
import {
    canSplitShared,
    devicesOnQuote,
    formatQty,
    groupLines,
    readGroupPref,
    untaggedLines,
    writeGroupPref,
    type Piece,
} from "./estimate-lines/model";
import { commercialDocStatusLabel } from "@/lib/vocabulary";
import { conversionToast, type ConversionScope } from "@/lib/estimate-conversion";
import { QboCustomerPickButton } from "@/components/qbo-customer-picker";
import { QuoteApprovalStrip } from "@/components/quote-second-look";
import { lineTaxBadge } from "@/lib/lineTaxBadge";

interface EstimateRow {
    id: string;
    display_number?: string | null;
    status: string;
    title: string;
    notes: string;
    /** Site the estimate is scoped to — drives the per-line device picker. */
    site_id?: string | null;
    /** That site's name/city, so the Details card can NAME the location rather
     *  than the page knowing only an invisible uuid (Issue #1024 follow-up). */
    site_name?: string | null;
    site_city?: string | null;
    subtotal: string;
    tax_amount?: string | null;
    tax_rate_pct?: string | null;
    tax_status_override?: string | null;
    tax_override_reason?: string | null;
    customer_tax_status?: string | null;
    customer_tax_exempt_reason?: string | null;
    created_at: string;
    updated_at: string;
    customer_name?: string;
    customer_id?: string | null;
    job_id?: string | null;
    source_job_title?: string | null;
    /** The originating job's site (Issue #1024). Close-out auto-estimates used
     *  to be written with no `site_id`, which left the per-line device picker
     *  permanently dead on a quote built FROM a job that knows its site. The
     *  picker falls back to this so those existing estimates work. */
    source_job_site_id?: string | null;
    source_job_site_name?: string | null;
    /** Per PRE-CUTOVER-UX-FEEDBACK.md #32: the originating-job summary
     *  card on the estimate detail screen needs number / status /
     *  scheduled date / tech name. Returned by GET /estimates/:id. */
    source_job_display_number?: string | null;
    source_job_status?: string | null;
    source_job_scheduled_start?: string | null;
    source_job_tech_name?: string | null;
    followup_job_id?: string | null;
    followup_job_title?: string | null;
    followup_job_status?: string | null;
    followup_job_display_number?: string | null;
    followup_job_scheduled_start?: string | null;
    /** ST import marker + translator state (ST → GW translation). */
    st_estimate_id?: string | null;
    translated_at?: string | null;
    /** Approval provenance (mig 331): set when the customer approved through the
     *  emailed no-login link — the name they typed + when. Null on an office-
     *  marked accept or a ServiceTitan import. */
    approved_by_name?: string | null;
    declined_at?: string | null;
    declined_by_name?: string | null;
    decline_reason_code?: string | null;
    decline_reason?: string | null;
    approved_via_token_at?: string | null;
    /** Post-send edit tracking (mig 524): content_edited_at is stamped on any
     *  customer-visible edit while `sent`; last_sent_at on every send/resend
     *  that actually emailed. edited_since_sent is server-computed — true only
     *  while `sent` with an edit newer than the last send. */
    content_edited_at?: string | null;
    last_sent_at?: string | null;
    edited_since_sent?: boolean;
    /** Vendor WO / customer PO on the QUOTE (mig 614, Issue #1623). Recordable
     *  at any status so the number is on file before a job exists. */
    external_ref?: string | null;
    customer_po?: string | null;
}

interface InvoiceRow {
    id: string;
    status: string;
    title: string;
    invoice_number: string;
    notes: string;
    subtotal: string;
    tax_amount: string;
    total_amount: string;
    tax_rate_pct?: string | null;
    tax_status_override?: string | null;
    tax_override_reason?: string | null;
    customer_tax_status?: string | null;
    customer_tax_exempt_reason?: string | null;
    created_at: string;
    updated_at: string;
    customer_name?: string;
    customer_id?: string | null;
    job_id?: string | null;
    estimate_id?: string | null;
    source_job_title?: string | null;
    source_estimate_title?: string | null;
    /** ServiceTitan import whose lines came over at $0 — the header total is
     *  the truth, so line editing is off and the PDF prints one summary line. */
    lines_not_itemized?: boolean;
    /** Charge-card-on-file button context. */
    payment_profile?: string | null;
    has_card_on_file?: boolean;
    card_brand?: string | null;
    card_last4?: string | null;
}

interface EstimateLine {
    id: string;
    description: string;
    /** Home or business city fee (migration 717). */
    fee_audience?: FeeAudienceInfo | null;
    qty: string;
    unit_price: string;
    line_total: string;
    tier?: string;
    /** Per-line tax override: null = inherit the SKU + kind rule. */
    taxable?: boolean | null;
    /** Server-computed effective taxability (residential labor → override → SKU → rule). */
    taxed?: boolean;
    /** Why: 'residential_labor' | 'by_hand' | 'item' | 'rule' (lineTaxBadge). */
    tax_reason?: string | null;
    /** Optional device (asset) this line quotes work on. */
    asset_id?: string | null;
    asset_name?: string | null;
    asset_serial?: string | null;
    asset_size?: string | null;
    /** #1441 — what "Tidy up" sorts on: 'labor' | 'material' | 'test_fee' | … */
    pricebook_kind?: string | null;
    /** Per-device repair option (#1120) this line is priced under, if any. */
    option_id?: string | null;
    /** Labor line hours × techs × rate (#1823 S3); null on other lines. */
    labor_hours?: string | null;
    labor_techs?: number | null;
    labor_rate?: string | null;
    /** Faster Estimates (migration 667). 'hand' = typed/edited by a person. */
    wording_source?: string | null;
    /** Plain words before the app reworded the line (the Undo pill). */
    reworded_from?: string | null;
    /** The "Whole job" group (trip charges) — never warns as untagged. */
    whole_job?: boolean | null;
    /** Bold / italic / bullets / numbers; null = plain. */
    description_html?: string | null;
    /** Server's read: labor | kit | test | fee | assembly | other. */
    wording_kind?: string | null;
    /** The words "Reword?" would write for this line's device. */
    reword_offer?: string | null;
    /** A replacement's check-first pipe line (pipe-choice-strip.tsx). */
    estimate_id?: string | null;
    pipe_choice_state?: string | null;
    pipe_choice_material?: string | null;
    pipe_choice_hint?: string | null;
    pipe_choice_why?: string | null;
    /** A NEW assembly line: what the customer reads + the make/model, office only (Corbin 2026-10-28). */
    office_assembly?: OfficeAssembly | null;
    /** Who put the line here (migration 716): 'gwc' | 'person'; null = an older line. */
    added_by?: string | null;
    /** Plain words for why GWC added it. */
    reason?: string | null;
    /** A person added or changed it: Tidy up, Re-price and Rebuild leave it alone. */
    user_touched?: boolean | null;
}

/** A device on the estimate's site, for the per-line link picker. */
interface SiteDeviceOption {
    id: string;
    name: string;
    size_label?: string | null;
    serial_number?: string | null;
    asset_kind?: string | null;
    manufacturer?: string | null;
    model?: string | null;
    assembly_type?: string | null;
    location_description?: string | null;
    /** Where the record came from (st:… = ServiceTitan). */
    external_key?: string | null;
}

/** Short human label for a linked device: "3/4" RP · #12345" style. */
function deviceLabel(d: {
    asset_name?: string | null;
    asset_size?: string | null;
    asset_serial?: string | null;
}): string {
    const parts = [d.asset_size, d.asset_name].filter((p): p is string => !!p && p.trim() !== "");
    const base = parts.join(" ").trim() || "Device";
    return d.asset_serial ? `${base} · #${d.asset_serial}` : base;
}

/** Same, for a device-picker option row. */
function deviceOptionLabel(d: SiteDeviceOption): string {
    const parts = [d.size_label, d.name].filter((p): p is string => !!p && p.trim() !== "");
    const base = parts.join(" ").trim() || "Device";
    return d.serial_number ? `${base} · #${d.serial_number}` : base;
}

/**
 * Per-line tax badge + inline toggle (Issue #229). Shows, in plain words, what
 * the tax engine does with the line and why (lineTaxBadge). When the doc is
 * editable, clicking forces the opposite treatment — except residential labor,
 * which Texas never taxes, so that badge is not a button.
 */
function TaxCell({
    line,
    editable,
    onToggle,
}: {
    line: { taxed?: boolean | null; taxable?: boolean | null; tax_reason?: string | null };
    editable: boolean;
    onToggle: (nextTaxable: boolean) => void;
}) {
    const b = lineTaxBadge(line);
    const badge = (
        <span
            className={`inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-xs font-medium ${
                b.taxed ? "bg-amber-100 text-amber-800" : "bg-muted text-muted-foreground"
            }`}
            title={b.title}
        >
            {b.label}
        </span>
    );
    if (!editable || !b.canToggle) return badge;
    return (
        <button
            type="button"
            className="hover:opacity-80"
            title={b.taxed ? "Mark this line non-taxable" : "Mark this line taxable"}
            onClick={() => onToggle(!b.taxed)}
        >
            {badge}
        </button>
    );
}

interface InvoiceLine {
    id: string;
    description: string;
    qty: string;
    unit_price: string;
    line_total: string;
    /** Per-line tax override: null = inherit the SKU + kind rule. */
    taxable?: boolean | null;
    /** Server-computed effective taxability (residential labor → override → SKU → rule). */
    taxed?: boolean;
    /** Why: 'residential_labor' | 'by_hand' | 'item' | 'rule' (lineTaxBadge). */
    tax_reason?: string | null;
    /** The device this line bills work on — what #1441 groups by. */
    asset_id?: string | null;
    /**
     * #1441 — derived server-side from the catalog row, because `invoice_lines`
     * never got estimate_lines' own `pricebook_kind` column. NULL where the line
     * has no pricebook item to read a kind from, and Tidy up leaves those alone.
     */
    pricebook_kind?: string | null;
    /** Labor line hours × techs × rate (#1823 S3); null on other lines. */
    labor_hours?: string | null;
    labor_techs?: number | null;
    labor_rate?: string | null;
    /** A NEW assembly line: what the customer reads + the make/model, office only (Corbin 2026-10-28). */
    office_assembly?: OfficeAssembly | null;
    /** Home or business city fee (migration 717). */
    fee_audience?: FeeAudienceInfo | null;
}

interface PaymentRow {
    id: string;
    amount: string;
    method: string;
    reference: string;
    paid_at: string;
}

interface QboSyncRow {
    id: string;
    status: "pending" | "in_progress" | "done" | "failed" | "needs_attention";
    attempt: number;
    last_error: string | null;
    next_attempt_at: string;
    updated_at: string;
}

function QboSyncBadge({
    row,
    onDrilldown,
    isAdmin,
}: {
    row: QboSyncRow | null;
    onDrilldown: () => void;
    /** C7: only admins can resolve QBO sync issues. Non-admins see a passive
     *  "escalate to admin" badge instead of a link into the admin-only page. */
    isAdmin: boolean;
}) {
    if (!row) return null;
    if (row.status === "done") {
        return (
            <Badge
                variant="outline"
                className="bg-green-50 text-green-700 border-green-300 dark:bg-green-950/40 dark:text-green-300"
            >
                <CheckCircle2 className="h-3 w-3 mr-1" /> Synced to QBO
            </Badge>
        );
    }
    if (row.status === "pending" || row.status === "in_progress") {
        return (
            <Badge
                variant="outline"
                className="bg-blue-50 text-blue-700 border-blue-300 dark:bg-blue-950/40 dark:text-blue-300"
            >
                <Loader2 className="h-3 w-3 mr-1 animate-spin" /> Pushing to QBO…
            </Badge>
        );
    }
    if (row.status === "failed") {
        return (
            <Badge
                variant="outline"
                className="bg-yellow-50 text-yellow-800 border-yellow-300 dark:bg-yellow-950/40 dark:text-yellow-300"
            >
                Push failed, retrying
            </Badge>
        );
    }
    if (row.status === "needs_attention") {
        if (!isAdmin) {
            return (
                <Badge
                    variant="outline"
                    className="bg-red-50 text-red-700 border-red-300 dark:bg-red-950/40 dark:text-red-300"
                    title="This invoice failed to sync to QuickBooks. Ask an admin to resolve it in QBO Sync Issues."
                >
                    <AlertTriangle className="h-3 w-3 mr-1" /> QBO sync issue — escalate to admin
                </Badge>
            );
        }
        return (
            <button onClick={onDrilldown} className="inline-flex">
                <Badge
                    variant="outline"
                    className="bg-red-50 text-red-700 border-red-300 dark:bg-red-950/40 dark:text-red-300 hover:underline cursor-pointer"
                >
                    <AlertTriangle className="h-3 w-3 mr-1" /> QBO push needs attention
                </Badge>
            </button>
        );
    }
    return null;
}

/** Mirrors DECLINE_REASON_LABEL in src/server/services/estimateDecline.ts —
 *  never render the raw code at a human. */
const DECLINE_REASON_LABELS: Record<string, string> = {
    price: "Price is too high",
    other_provider: "Going with someone else",
    not_now: "Not right now",
    other: "Something else",
};
function declineReasonLabel(code: string | null | undefined): string | null {
    if (!code) return null;
    return DECLINE_REASON_LABELS[code] ?? code.replace(/_/g, " ");
}

export default function CommercialDetailPage({
    forceType,
}: {
    /** When set, locks the page to a document kind regardless of the `?type`
     *  query param. The dedicated `/invoices/:invoiceId` route passes
     *  `forceType="invoice"`; `/commercial/:id` leaves it unset and falls back
     *  to the query param (estimate by default). */
    forceType?: "estimate" | "invoice";
} = {}) {
    const params = useParams<{ id?: string; invoiceId?: string }>();
    const id = params.id ?? params.invoiceId;
    // `/invoices/new` (and any other non-uuid id) matches this route's :id
    // param — without this guard the page fetched `/api/invoices/new` and
    // crashed into the error boundary. Redirect handled below, after hooks.
    const isValidDocId = !!id && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);
    const [searchParams] = useSearchParams();
    const docType = forceType ?? (searchParams.get("type") || "estimate");
    const qc = useQueryClient();
    const navigate = useNavigate();
    const { isAdmin, can } = useAuth();
    const canManagePayments = isAdmin || can("payments.manage");
    // Local date (YYYY-MM-DD) for the Mark Paid form's default "collected on".
    const todayIso = new Date().toLocaleDateString("en-CA");

    const isEstimate = docType === "estimate";
    const [previewOpen, setPreviewOpen] = useState(false);

    const {
        data: estimateData,
        isLoading: loadingEstimate,
        error: estimateError,
        refetch: refetchEstimate,
    } = useQuery({
        queryKey: ["estimate", id],
        queryFn: async () => {
            const r = await api.get<{
                estimate: EstimateRow;
                lines: EstimateLine[];
                // #1625 — the options and the customer's answer. Absent on a
                // flat estimate, which then renders exactly as it always has.
                optionView?: EstimateOptionView;
                invoiceMismatch?: InvoiceMismatch | null;
            }>(`/api/estimates/${id}`);
            return {
                estimate: r.estimate,
                lines: r.lines ?? [],
                optionView: r.optionView ?? { options: [], deviceGroups: null },
                invoiceMismatch: r.invoiceMismatch ?? null,
            };
        },
        enabled: isValidDocId && isEstimate,
    });

    const {
        data: invoiceData,
        isLoading: loadingInvoice,
        error: invoiceError,
        refetch: refetchInvoice,
    } = useQuery({
        queryKey: ["invoice", id],
        queryFn: async () => {
            // The server's GET /invoices/:id already returns invoice + lines +
            // payments in a single payload — the previous code split this into
            // three calls and 404'd on the missing line/payment GETs.
            const r = await api.get<{
                invoice: InvoiceRow;
                lines: InvoiceLine[];
                payments: PaymentRow[];
            }>(`/api/invoices/${id}`);
            return { invoice: r.invoice, lines: r.lines ?? [], payments: r.payments ?? [] };
        },
        enabled: isValidDocId && !isEstimate,
    });

    // Support Copilot page context — Help names the document unprompted.
    const ctxEstimate = estimateData?.estimate;
    const ctxInvoice = invoiceData?.invoice;
    usePageContextReporter(
        isEstimate && ctxEstimate
            ? {
                  label: `Estimate ${ctxEstimate.display_number || ctxEstimate.id.slice(0, 8)}${ctxEstimate.title ? ` — ${ctxEstimate.title}` : ""}`,
                  entity: { type: "estimates", id: ctxEstimate.id },
                  values: { status: ctxEstimate.status },
              }
            : !isEstimate && ctxInvoice
              ? {
                    label: `Invoice ${ctxInvoice.invoice_number || ctxInvoice.id.slice(0, 8)}${ctxInvoice.title ? ` — ${ctxInvoice.title}` : ""}`,
                    entity: { type: "invoices", id: ctxInvoice.id },
                    values: { status: ctxInvoice.status },
                }
              : null,
    );

    // Compose/preview dialog for the customer-facing Send (recipients + editable
    // subject/body) — replaces the old instant one-click send.
    const [sendOpen, setSendOpen] = useState(false);

    // EST-1: Cancel Quote confirmation (required reason note).
    const [cancelQuoteOpen, setCancelQuoteOpen] = useState(false);
    const [reopenOpen, setReopenOpen] = useState(false);
    const [recordApprovalOpen, setRecordApprovalOpen] = useState(false);

    // Void Invoice confirmation (required reason). Voiding an invoice that was
    // finalized/"sent" by mistake is what unblocks cancelling its job.
    const [voidInvoiceOpen, setVoidInvoiceOpen] = useState(false);

    // P0 (CUTOVER-BUGS.md 2026-07-12): the doc editor used to swallow failed
    // saves — typed values stayed on screen looking saved while the API
    // 500'd. Every editor mutation surfaces its error; line edits also close
    // the editing cell so it reverts to the stored value.
    const apiErrMsg = (e: any) => e?.body?.message ?? e?.body?.error ?? e?.message ?? "unknown";

    const copyPayLink = useMutation({
        mutationFn: () => api.get<{ url: string }>(`/api/invoices/${id}/pay-link`),
        onSuccess: async (r) => {
            try {
                await navigator.clipboard.writeText(r.url);
                toast.success("Payment link copied to clipboard");
            } catch {
                toast.message("Payment link", { description: r.url });
            }
        },
        onError: (e: any) => toast.error(`Could not create pay link: ${e?.body?.error ?? "unknown"}`),
    });

    const createFollowUpJob = useMutation({
        mutationFn: () =>
            api.post<
                ConversionScope & {
                    job: { id: string; status: string; title: string };
                    invoice: { id: string } | null;
                }
            >(`/api/estimates/${id}/create-followup-job`, {}),
        onSuccess: (r) => {
            const t = conversionToast(r, r?.invoice ? "Job and invoice created" : "Follow-up job created");
            if (t.warn) toast.warning(t.text, { duration: 12000 });
            else toast.success(r?.invoice ? `${t.text}; invoice created` : t.text);
            qc.invalidateQueries({ queryKey: ["commercial"] });
            qc.invalidateQueries({ queryKey: ["estimate", id] });
            if (r?.job?.id) navigate(`/jobs/${r.job.id}`);
        },
        onError: (e: any) =>
            toast.error(`Job creation failed: ${e?.body?.message ?? e?.body?.error ?? e?.message ?? "unknown"}`),
    });

    // H21: convert an accepted estimate straight into an invoice without first
    // creating a job — previously only reachable from Office Review.
    const convertToInvoice = useMutation({
        mutationFn: () => api.post<{ invoice: { id: string } }>(`/api/estimates/${id}/convert-to-invoice`, {}),
        onSuccess: (r) => {
            toast.success("Draft invoice made. It goes out from Close Out when the work is done.");
            qc.invalidateQueries({ queryKey: ["commercial"] });
            qc.invalidateQueries({ queryKey: ["estimate", id] });
            if (r?.invoice?.id) navigate(`/invoices/${r.invoice.id}`);
        },
        onError: (e: any) =>
            toast.error(`Convert failed: ${e?.body?.message ?? e?.body?.error ?? e?.message ?? "unknown"}`),
    });

    // "Create PO from estimate" — anytime, idempotent (the server returns
    // the existing live PO instead of duplicating).
    const createPo = useMutation({
        mutationFn: () =>
            api.post<{
                created?: boolean;
                alreadyExists?: boolean;
                poId: string | null;
                poNumber: string | null;
                orderableCount?: number;
                nonOrderable?: string[];
            }>(`/api/estimates/${id}/create-po`, {}),
        onSuccess: (r) => {
            if (r.alreadyExists) {
                toast.info(`PO ${r.poNumber ?? ""} already exists for this estimate — opening Purchasing.`);
            } else if (r.poId) {
                const manual = r.nonOrderable?.length
                    ? ` (${r.nonOrderable.length} item${r.nonOrderable.length === 1 ? "" : "s"} need manual sourcing)`
                    : "";
                toast.success(`Draft PO ${r.poNumber ?? ""} created — ${r.orderableCount} item(s) ready${manual}.`);
            } else {
                toast.warning(
                    r.nonOrderable?.length
                        ? "No orderable lines — materials have no vendor SKU. Source manually."
                        : "No material lines on this estimate — translate/link lines to the pricebook first.",
                );
            }
            qc.invalidateQueries({ queryKey: ["purchasing"] });
        },
        onError: (e: any) => toast.error(`PO failed: ${e?.body?.message ?? e?.body?.error ?? e?.message ?? "unknown"}`),
    });

    const [paymentOpen, setPaymentOpen] = useState(false);
    const [paymentForm, setPaymentForm] = useState({ method: "Check", reference: "", amount: "", date: todayIso });

    // Mark Paid / Record Payment both open this one form. It always POSTs
    // through record-payment, which inserts an invoice_payments row — there is
    // no path that flips an invoice to paid without a payment record (that was
    // the phantom-paid incident's root cause). The form carries method,
    // reference, the date money was collected, and the amount.
    const openMarkPaidForm = () =>
        setPaymentForm({ method: "Check", reference: "", amount: remainingBalance.toFixed(2), date: todayIso });

    const recordPayment = useMutation({
        mutationFn: (body: { payment_method: string; payment_reference?: string; amount?: number; paid_at?: string }) =>
            api.post(`/api/invoices/${id}/record-payment`, body),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["invoice", id] });
            toast.success("Payment recorded");
            setPaymentOpen(false);
            setPaymentForm({ method: "Check", reference: "", amount: "", date: todayIso });
        },
        onError: (e: any) =>
            toast.error(`Payment failed: ${e?.body?.message ?? e?.body?.error ?? e?.message ?? "unknown"}`),
    });

    // REOPEN / UNDO a mistaken Mark Paid. Server refuses when a real Stripe
    // charge is on file (use Refund instead), so this is safe for the
    // collected-elsewhere case.
    const reopenInvoice = useMutation({
        mutationFn: () => api.post(`/api/invoices/${id}/reopen`, {}),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["invoice", id] });
            toast.success("Invoice reopened — payment records removed");
        },
        onError: (e: any) =>
            toast.error(`Reopen failed: ${e?.body?.message ?? e?.body?.error ?? e?.message ?? "unknown"}`),
    });

    const [refundOpen, setRefundOpen] = useState(false);
    const [refundForm, setRefundForm] = useState({ amount: "", reason: "requested_by_customer", note: "" });

    const refund = useMutation({
        mutationFn: (b: { amountCents?: number; reason?: string; note?: string }) =>
            api.post(`/api/payments/stripe/refund`, { invoiceId: id, ...b }),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["invoice", id] });
            toast.success("Refund issued — QBO RefundReceipt queued");
            setRefundOpen(false);
            setRefundForm({ amount: "", reason: "requested_by_customer", note: "" });
        },
        onError: (e: any) =>
            toast.error(`Refund failed: ${e?.body?.message ?? e?.body?.error ?? e?.message ?? "unknown"}`),
    });

    const [editingLine, setEditingLine] = useState<{
        id: string;
        description: string;
        qty: string;
        unit_price: string;
        // Estimate lines only: null = explicitly no device. Invoice edits omit it.
        asset_id?: string | null;
        // Estimate lines only (Faster Estimates): the rich words box.
        html?: string;
        htmlStart?: string;
        whole_job?: boolean;
    } | null>(null);

    // Faster Estimates: short-lived pills on lines this visit touched
    // ("Tagged + reworded", "Split from qty 2", "Added"), the "Group by
    // device" switch, and the send warning.
    const [lineChips, setLineChips] = useState<Record<string, string>>({});
    const [groupByDevice, setGroupByDevice] = useState<boolean>(() => readGroupPref());
    const [tagMenuOpen, setTagMenuOpen] = useState(false);
    const [sendGuardOpen, setSendGuardOpen] = useState(false);
    const [repairMenuOpen, setRepairMenuOpen] = useState(false);
    const [groupedEditId, setGroupedEditId] = useState<string | null>(null);

    // When set, the AddDeviceToSiteModal is open for this line — the created
    // device is linked to it on success.
    const [addDeviceForLine, setAddDeviceForLine] = useState<string | null>(null);

    // Devices for the per-line link picker. Assets live on sites, so the
    // estimate has to resolve to one — its own site when it has it, otherwise
    // the originating job's (Issue #1024: close-out auto-estimates were born
    // with no site_id, which killed this picker permanently).
    const deviceScope = estimateDeviceScope(estimateData?.estimate);
    const estSiteId = deviceScope.siteId;
    const siteDevicesQuery = useQuery({
        queryKey: ["site-assets-for-lines", estSiteId],
        queryFn: () => api.get<{ assets: SiteDeviceOption[] }>(`/api/sites/${estSiteId}/assets?limit=200`),
        enabled: isEstimate && !!estSiteId,
    });
    const siteDevices = siteDevicesQuery.data?.assets ?? [];

    const patchEstimateLine = useMutation({
        mutationFn: ({
            lineId,
            ...body
        }: {
            lineId: string;
            description?: string;
            qty?: number;
            unit_price?: number;
            assetId?: string | null;
            taxable?: boolean | null;
            labor_hours?: number;
            labor_techs?: number;
            description_html?: string;
            whole_job?: boolean;
        }) =>
            api.patch<{ ok: true; reworded: { before: string | null; after: string | null } | null }>(
                `/api/estimates/${id}/lines/${lineId}`,
                body,
            ),
        // #1139: `invalidateQueries` only SCHEDULES a refetch. Closing the
        // editor on the same tick flipped the cell back to the still-stale
        // cached row, so the OLD price showed for one round-trip before the
        // new one landed — on every edit, reading as "the app lost my
        // change". Awaiting it means the cell only reopens on fresh data.
        onSuccess: async (r, v) => {
            await qc.invalidateQueries({ queryKey: ["estimate", id] });
            setEditingLine(null);
            if (r?.reworded) setLineChips((c) => ({ ...c, [v.lineId]: "Moved + reworded" }));
            else if (v.description !== undefined || v.description_html !== undefined)
                // Typed by hand now: no stale "Reworded" pill on it.
                setLineChips((c) => {
                    const { [v.lineId]: _gone, ...rest } = c;
                    return rest;
                });
            toast.success("Line updated");
        },
        onError: (e: any) => {
            // Revert the cell to the stored value — never leave a failed edit
            // on screen looking saved.
            setEditingLine(null);
            qc.invalidateQueries({ queryKey: ["estimate", id] });
            toast.error(`Line NOT saved: ${apiErrMsg(e)}`);
        },
    });

    /**
     * #1441 — deleting a line, including the awkward one.
     *
     * #1417 refuses to delete an option's LAST line, because an option with no
     * lines renders as a selectable $0.00 repair a customer can accept for free.
     * That refusal was right, but on THIS screen it was a dead end: the "remove
     * the whole option" escape was only ever built into Closeout, so Corbin hit
     * a wall here fixing Fort Worth ISD 092678173 by hand — "the problem now is
     * i can't delete everything" — and then set the rule for this feature: "i
     * don't want it a hard rule to where i cannot delete a part off of the
     * estimate/invoice".
     *
     * So the refusal now becomes a question. The server answers a plain delete
     * with `cascadeAvailable`, we ask once naming the option that would go, and
     * on yes we retry with `?cascade=1`. The guard still catches the mistake; it
     * just no longer has the last word.
     */
    const deleteEstimateLine = useMutation({
        mutationFn: async (lineId: string) => {
            try {
                return await api.delete(`/api/estimates/${id}/lines/${lineId}`);
            } catch (e: any) {
                const body = e?.body ?? {};
                if (body.error !== "OPTION_WOULD_BE_EMPTY" || !body.cascadeAvailable) throw e;
                const label = body.optionLabel ?? "this repair option";
                const goAhead = await confirmDialog({
                    message: `That's the last line on "${label}". Deleting it removes the whole "${label}" option from the quote — the customer will no longer be offered it. The rest of the estimate is untouched.`,
                    destructive: true,
                    confirmLabel: `Delete the line and "${label}"`,
                });
                if (!goAhead) return { cancelled: true } as const;
                return await api.delete(`/api/estimates/${id}/lines/${lineId}?cascade=1`);
            }
        },
        onSuccess: (r: any) => {
            if (r?.cancelled) return;
            qc.invalidateQueries({ queryKey: ["estimate", id] });
            toast.success(
                r?.removedOptionLabel ? `Line removed, along with "${r.removedOptionLabel}"` : "Line removed",
            );
        },
        onError: (e: any) => toast.error(`Line NOT removed: ${apiErrMsg(e)}`),
    });

    // Add a blank freeform line, then drop straight into edit mode on it — the
    // manual "type it yourself" path (the pricebook picker covers catalog work).
    // No sortOrder on any of the adds: the server puts a new line at the bottom
    // (the bottom of its own device group when grouped).
    const addCustomEstimateLine = useMutation({
        mutationFn: () =>
            api.post<{ line: EstimateLine }>(`/api/estimates/${id}/lines`, {
                lineType: "custom",
                description: "New line",
                qty: 1,
                unitPrice: 0,
            }),
        onSuccess: (r) => {
            qc.invalidateQueries({ queryKey: ["estimate", id] });
            if (groupByDevice) {
                setGroupedEditId(r.line.id);
                return;
            }
            const start = initialRichHtml(null, r.line.description);
            setEditingLine({
                id: r.line.id,
                description: r.line.description,
                qty: r.line.qty,
                unit_price: r.line.unit_price,
                asset_id: r.line.asset_id ?? null,
                html: start,
                htmlStart: start,
            });
        },
        onError: (e: any) => toast.error(`Couldn't add line: ${apiErrMsg(e)}`),
    });

    // A labor line priced from the customer's card (#1823 S2/S3): 1 h × 1 tech
    // to start, then the stepper sets the real hours and crew.
    const addLaborEstimateLine = useMutation({
        mutationFn: () =>
            api.post<{ line: EstimateLine }>(`/api/estimates/${id}/lines`, {
                lineType: "custom",
                description: "",
                qty: 1,
                unitPrice: 0,
                labor: { hours: 1, techs: 1 },
            }),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["estimate", id] });
            toast.success("Labor line added at the customer's rate");
        },
        onError: (e: any) => toast.error(`Couldn't add labor: ${apiErrMsg(e)}`),
    });

    const patchInvoiceLine = useMutation({
        mutationFn: ({
            lineId,
            ...body
        }: {
            lineId: string;
            description?: string;
            qty?: number;
            unit_price?: number;
            taxable?: boolean | null;
            labor_hours?: number;
            labor_techs?: number;
        }) => api.patch(`/api/invoices/${id}/lines/${lineId}`, body),
        // #1139: same as the estimate editor above — await the refetch
        // before closing, or the cell renders the stale cached price first.
        onSuccess: async () => {
            await qc.invalidateQueries({ queryKey: ["invoice", id] });
            setEditingLine(null);
            toast.success("Line updated");
        },
        onError: (e: any) => {
            setEditingLine(null);
            qc.invalidateQueries({ queryKey: ["invoice", id] });
            toast.error(`Line NOT saved: ${apiErrMsg(e)}`);
        },
    });

    const deleteInvoiceLine = useMutation({
        mutationFn: (lineId: string) => api.delete(`/api/invoices/${id}/lines/${lineId}`),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["invoice", id] });
            toast.success("Line removed");
        },
        onError: (e: any) => toast.error(`Line NOT removed: ${apiErrMsg(e)}`),
    });

    const [headerEdit, setHeaderEdit] = useState<{
        title: string;
        notes: string;
        /** Estimates only — "" means no site. Invoices ignore this field. */
        site_id?: string;
        /** Estimates only (Issue #1623) — the vendor WO / customer PO. */
        external_ref?: string;
        customer_po?: string;
        tax_rate: string;
        payment_terms: string;
    } | null>(null);

    // Locations belonging to this estimate's customer, for the Edit Details
    // site picker (Issue #1024 — there was previously no way to set an
    // estimate's site anywhere in the app).
    const estCustomerId = estimateData?.estimate?.customer_id ?? null;
    const customerSitesQuery = useQuery({
        queryKey: ["customer-sites", estCustomerId],
        queryFn: () =>
            api.get<{ sites: Array<{ id: string; name: string; city?: string | null }> }>(
                `/api/customers/${estCustomerId}/sites?limit=100`,
            ),
        enabled: isEstimate && !!estCustomerId && !!headerEdit,
    });
    const customerSites = customerSitesQuery.data?.sites ?? [];

    const saveEstimateHeader = useMutation({
        mutationFn: (body: {
            title?: string;
            notes?: string;
            siteId?: string | null;
            externalRef?: string | null;
            customerPo?: string | null;
        }) => api.patch(`/api/estimates/${id}`, body),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["estimate", id] });
            setHeaderEdit(null);
            toast.success("Details updated");
        },
        onError: (e: any) => toast.error(`Details NOT saved: ${apiErrMsg(e)}`),
    });

    const saveInvoiceHeader = useMutation({
        mutationFn: (body: { title?: string; notes?: string; taxAmount?: number }) =>
            api.patch(`/api/invoices/${id}`, body),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["invoice", id] });
            setHeaderEdit(null);
            toast.success("Details updated");
        },
        onError: (e: any) => toast.error(`Details NOT saved: ${apiErrMsg(e)}`),
    });

    // QBO sync state — poll the queue for 30s after a Push to QBO click
    // so the badge picks up the worker tick without a manual refresh.
    // Outside the polling window we drop to a stale read on demand.
    const [pollUntil, setPollUntil] = useState<number>(0);
    const polling = !isEstimate && Date.now() < pollUntil;
    const qboSyncStatusQuery = useQuery<{ row: QboSyncRow | null }>({
        queryKey: ["qbo-sync-status", "invoice", id],
        queryFn: () =>
            api.get<{ row: QboSyncRow | null }>(
                `/api/integrations/quickbooks/sync-status?entity=invoice&entityId=${id}`,
            ),
        enabled: !!id && !isEstimate,
        refetchInterval: polling ? 5_000 : false,
    });
    const pushToQbo = useMutation({
        mutationFn: () => api.post(`/api/integrations/quickbooks/sync-now`, { entity: "invoice", entityId: id }),
        onSuccess: () => {
            toast.success("Push to QBO queued");
            setPollUntil(Date.now() + 30_000);
            qc.invalidateQueries({ queryKey: ["qbo-sync-status", "invoice", id] });
        },
        onError: (e: any) =>
            toast.error(`Push failed: ${e?.body?.message ?? e?.body?.error ?? e?.message ?? "unknown"}`),
    });

    // #1441 — drag-to-reorder for both line tables. Declared up here with every
    // other hook, ABOVE the early returns below, because the estimate and
    // invoice branches each return before the other's markup is reached and a
    // hook called inside one of them would change hook order between renders.
    const estimateLinesRaw: EstimateLine[] = estimateData?.lines ?? [];
    // #1625: an optioned estimate shows its answer above, so the flat table
    // below is the full list rather than the only list — and says so.
    const optionView = estimateData?.optionView;
    const hasOptionGroups = Boolean(
        (optionView?.deviceGroups && optionView.deviceGroups.length > 0) || (optionView?.options?.length ?? 0) > 0,
    );
    /** What the customer actually bought, when they have answered. */
    const soldSummary = (() => {
        const groups = optionView?.deviceGroups ?? null;
        if (!groups || groups.length === 0) return null;
        const chosen = groups
            .map((g) => g.options.find((o) => o.isSelected) ?? null)
            .filter((o): o is NonNullable<typeof o> => o !== null);
        if (chosen.length === 0) return null;
        const bought = chosen.filter((o) => !o.isDecline);
        return {
            deviceCount: bought.length,
            declined: chosen.length - bought.length,
            label: bought.length === 1 ? bought[0].label : null,
        };
    })();
    const invoiceLinesRaw: InvoiceLine[] = invoiceData?.lines ?? [];
    const estimateReorder = useLineReorder<EstimateLine>({
        lines: estimateLinesRaw,
        enabled:
            !!estimateData?.estimate && !["accepted", "declined", "cancelled"].includes(estimateData.estimate.status),
        save: (lineIds) => api.put(`/api/estimates/${id}/lines/order`, { lineIds }),
    });
    const invoiceReorder = useLineReorder<InvoiceLine>({
        lines: invoiceLinesRaw,
        enabled: !!invoiceData?.invoice && !["paid", "void"].includes(invoiceData.invoice.status),
        save: (lineIds) => api.put(`/api/invoices/${id}/lines/order`, { lineIds }),
    });

    // Tick lines → "Delete N lines" → Undo (#1823 S4).
    const estimateBulk = useBulkLineDelete({
        docType: "estimate",
        docId: id ?? "",
        lineIds: estimateLinesRaw.map((l) => l.id),
        onChanged: () => qc.invalidateQueries({ queryKey: ["estimate", id] }),
    });
    // Drop a choice from this quote, with Undo (2026-10-28).
    const optionRemoval = useOptionRemoval({
        estimateId: id ?? "",
        onChanged: () => qc.invalidateQueries({ queryKey: ["estimate", id] }),
    });
    const invoiceBulk = useBulkLineDelete({
        docType: "invoice",
        docId: id ?? "",
        lineIds: invoiceLinesRaw.map((l) => l.id),
        onChanged: () => qc.invalidateQueries({ queryKey: ["invoice", id] }),
    });
    // "Add repair for device ▾" (#1823 S4): the device's repair priced from the
    // customer's card — size-table hours × techs × rate, kit, re-test, fees.
    const addRepairForDevice = useMutation({
        mutationFn: (v: { assetId: string; kind: "repair" | "replace" }) =>
            api.post<{
                source: "pricebook" | "labor_only" | "broken_out";
                insertedLineIds: string[];
                labor: LaborShape | null;
            }>(`/api/estimates/${id}/lines/repair-for-device`, v),
        onSuccess: async (r) => {
            await qc.invalidateQueries({ queryKey: ["estimate", id] });
            const labor = r.labor
                ? ` — ${r.labor.hours} h × ${r.labor.techs} tech${r.labor.techs === 1 ? "" : "s"}`
                : "";
            toast.success(
                r.source === "broken_out"
                    ? `Added the replacement, broken out into ${r.insertedLineIds.length} lines${labor}`
                    : r.source === "pricebook"
                      ? `Added ${r.insertedLineIds.length} lines${labor}`
                      : `Added the labor line${labor}. No repair item fits this device — add the parts.`,
            );
        },
        onError: (e: any) => toast.error(`Couldn't add the repair: ${apiErrMsg(e)}`),
    });

    // ── Faster Estimates (approved mockup, Sleeker v2) ────────────────────
    const chip = (ids: string[], text: string) =>
        setLineChips((c) => {
            const next = { ...c };
            for (const lineId of ids) next[lineId] = text;
            return next;
        });
    const refreshEstimate = () => qc.invalidateQueries({ queryKey: ["estimate", id] });

    /** Tag lines to a device / Whole job / no device. The app's lines reword. */
    const tagLinesMut = useMutation({
        mutationFn: (v: { lineIds: string[]; pick: DeviceMenuPick }) =>
            api.post<{
                lines: Array<{
                    id: string;
                    moved: boolean;
                    reworded: boolean;
                    hand: boolean;
                    offer: string | null;
                }>;
            }>(`/api/estimates/${id}/lines/tag`, {
                lineIds: v.lineIds,
                assetId: v.pick.kind === "device" ? v.pick.device.id : null,
                wholeJob: v.pick.kind === "whole",
            }),
        onSuccess: async (r, v) => {
            await refreshEstimate();
            setLineChips((c) => {
                const next = { ...c };
                for (const l of r.lines) {
                    if (!l.moved && !l.reworded) continue;
                    next[l.id] = l.reworded
                        ? v.pick.kind === "device" && l.moved
                            ? "Tagged + reworded"
                            : "Reworded"
                        : v.pick.kind === "none"
                          ? "Untagged"
                          : "Tagged";
                }
                return next;
            });
            estimateBulk.clear();
            setTagMenuOpen(false);
            const n = v.lineIds.length;
            const where =
                v.pick.kind === "device"
                    ? `${[v.pick.device.size_label ? `${v.pick.device.size_label.replace(/"$/, "")}"` : null, v.pick.device.manufacturer ?? v.pick.device.name].filter(Boolean).join(" ")}`
                    : v.pick.kind === "whole"
                      ? "Whole job"
                      : "no device";
            const kept = r.lines.filter((l) => l.hand && l.offer).length;
            toast.success(
                `${n} line${n === 1 ? "" : "s"} tagged to ${where}${kept ? ` · ${kept} typed by hand kept their words` : ""}`,
            );
        },
        onError: (e: any) => toast.error(`Lines NOT tagged: ${apiErrMsg(e)}`),
    });

    /** A shared fee (qty N) → one per device, same total. */
    const splitLineMut = useMutation({
        mutationFn: (v: { lineId: string; assetIds: string[] }) =>
            api.post<{ lineIds: string[] }>(`/api/estimates/${id}/lines/${v.lineId}/split`, { assetIds: v.assetIds }),
        onSuccess: async (r, v) => {
            await refreshEstimate();
            chip(r.lineIds, `Split from qty ${v.assetIds.length}`);
            estimateBulk.clear();
            toast.success("Split: 1 per device, same total");
        },
        onError: (e: any) => toast.error(`Line NOT split: ${apiErrMsg(e)}`),
    });

    const rewordMut = useMutation({
        mutationFn: (lineId: string) => api.post(`/api/estimates/${id}/lines/${lineId}/reword`, {}),
        onSuccess: async (_r, lineId) => {
            await refreshEstimate();
            chip([lineId], "Reworded");
        },
        onError: (e: any) => toast.error(`Couldn't reword: ${apiErrMsg(e)}`),
    });

    const undoRewordMut = useMutation({
        mutationFn: (lineId: string) => api.post(`/api/estimates/${id}/lines/${lineId}/undo-reword`, {}),
        onSuccess: async (_r, lineId) => {
            await refreshEstimate();
            chip([lineId], "Wording put back");
        },
        onError: (e: any) => toast.error(`Undo failed: ${apiErrMsg(e)}`),
    });

    /** One-tap "add the missing piece", priced by the Add-repair engine. */
    const addMissingMut = useMutation({
        mutationFn: (v: { assetId: string; piece: Piece; kind: "repair" | "replace" }) =>
            api.post<{ insertedLineIds: string[] }>(`/api/estimates/${id}/lines/repair-for-device`, {
                assetId: v.assetId,
                kind: v.kind,
                only: [v.piece],
            }),
        onSuccess: async (r) => {
            await refreshEstimate();
            chip(r.insertedLineIds, "Added");
            toast.success(r.insertedLineIds.length ? "Added" : "Nothing to add for that device");
        },
        onError: (e: any) => toast.error(`Couldn't add it: ${apiErrMsg(e)}`),
    });

    /** "+" on a device header: a typed line already tagged to that device. */
    const addLineToDevice = useMutation({
        mutationFn: (assetId: string) =>
            api.post<{ line: EstimateLine }>(`/api/estimates/${id}/lines`, {
                lineType: "custom",
                description: "New line",
                qty: 1,
                unitPrice: 0,
                assetId,
            }),
        onSuccess: async (r) => {
            await refreshEstimate();
            setGroupedEditId(r.line.id);
        },
        onError: (e: any) => toast.error(`Couldn't add line: ${apiErrMsg(e)}`),
    });

    // The device header's missing-piece check: what the Add-repair engine
    // would bring for each device whose repair has a gap — priced, not written.
    const lineSections = useMemo(
        () => (isEstimate ? groupLines(estimateLinesRaw, siteDevices) : []),
        [isEstimate, estimateLinesRaw, siteDevices],
    );
    const gapDevices = lineSections.filter(
        (s) => s.kind === "device" && s.device && (!s.has.labor || !s.has.kit || !s.has.test || !s.has.fee),
    );
    const workKindFor = (assetId: string): "repair" | "replace" =>
        estimateLinesRaw.some((l) => l.asset_id === assetId && /\breplace/i.test(l.description)) ? "replace" : "repair";
    const previewQueries = useQueries({
        queries: gapDevices.map((s) => ({
            queryKey: ["repair-preview", id, s.device!.id, workKindFor(s.device!.id), estimateLinesRaw.length],
            queryFn: () =>
                api.get<{ preview: PiecePreview[] }>(
                    `/api/estimates/${id}/lines/repair-for-device/preview?assetId=${s.device!.id}&kind=${workKindFor(s.device!.id)}`,
                ),
            enabled:
                isEstimate &&
                groupByDevice &&
                !!estimateData?.estimate &&
                !["accepted", "declined", "cancelled"].includes(estimateData.estimate.status),
            staleTime: 60_000,
            retry: false,
        })),
    });
    const piecePreviews = new Map<string, PiecePreview[] | null>(
        gapDevices.map((s, k) => [s.device!.id, previewQueries[k]?.data?.preview ?? null]),
    );

    // Placed after every hook so the hook order stays stable if the route
    // param changes in place (e.g. /invoices/new -> /invoices/<uuid>).
    if (!isValidDocId) {
        return <Navigate to="/commercial" replace />;
    }

    const isLoading = isEstimate ? loadingEstimate : loadingInvoice;
    if (isLoading) return <PageSpinner />;

    if (isEstimate) {
        const est = estimateData?.estimate;
        if (!est)
            return (
                <DetailErrorState
                    error={estimateError}
                    onRetry={() => void refetchEstimate()}
                    notFoundTitle="Estimate not found"
                    listHref="/commercial"
                    listLabel="Back to documents"
                />
            );
        const canEditLines = !["accepted", "declined", "cancelled"].includes(est.status);
        // Decision 3 (Faster Estimates): lines with no device warn at send,
        // never block. Only when the location has devices to tag them to.
        const untagged = siteDevices.length > 0 ? untaggedLines(estimateLinesRaw) : [];
        const requestSend = () => {
            if (untagged.length > 0) setSendGuardOpen(true);
            else setSendOpen(true);
        };
        const quoteDeviceIds = devicesOnQuote(estimateLinesRaw);
        const selectedLines = estimateLinesRaw.filter((l) => estimateBulk.selected.has(l.id));
        const splittableSelected = selectedLines.filter((l) => canSplitShared(l, quoteDeviceIds));
        const lineDevices = siteDevices;
        // Reopen-to-correct (Issue #570): an issued estimate can be dropped back
        // to draft to fix a mistake — admin/finance only, and only from a
        // customer-facing terminal state (accepted/declined). Cancelled is a
        // deliberate dead exit; draft/sent are already editable.
        // Reopening an ACCEPTED estimate un-commits money (admin/finance).
        // Reopening a DECLINED one commits nothing — it's re-quoting a lost
        // sale, which any office user should be able to chase (#1124).
        const canReopen =
            est.status === "declined" ? true : (isAdmin || can("finance.manage")) && est.status === "accepted";
        const lineCols: Column<EstimateLine>[] = [
            ...(canEditLines
                ? [
                      {
                          key: "select" as keyof EstimateLine & string,
                          header: "",
                          className: "w-8 pr-0",
                          render: (r: EstimateLine) => (
                              <LineCheckbox bulk={estimateBulk} id={r.id} label={r.description} />
                          ),
                      },
                  ]
                : []),
            ...(canEditLines
                ? [
                      {
                          key: "reorder" as keyof EstimateLine & string,
                          header: "",
                          className: "w-8 pr-0",
                          render: (r: EstimateLine) => estimateReorder.renderHandle(r),
                      },
                  ]
                : []),
            {
                key: "description",
                header: "Description",
                render: (r) =>
                    editingLine?.id === r.id ? (
                        <RichLineEditor
                            initialHtml={editingLine.htmlStart ?? ""}
                            onChange={(html) => setEditingLine((cur) => (cur ? { ...cur, html } : cur))}
                            autoFocus
                        />
                    ) : (
                        <div className="flex flex-col gap-1.5">
                            <RichLineText html={r.description_html} text={r.description} />
                            <PipeChoiceStrip line={r} canEdit={canEditLines} />
                            <OfficeOnlyAssemblyNote note={r.office_assembly} />
                            {/* Who added it and why (slice C, migration 716). Office only. */}
                            <LineOriginTag line={r} className="self-start" />
                            <FeeAudiencePick line={r} doc="estimate" docId={id} canEdit={canEditLines} />
                            {/* Labor line: change the crew or hours right here (#1823 S3). */}
                            {canEditLines && laborOf(r) && (
                                <LaborCrewStepper
                                    labor={laborOf(r)!}
                                    pending={
                                        patchEstimateLine.isPending && patchEstimateLine.variables?.lineId === r.id
                                    }
                                    onChange={(next) => patchEstimateLine.mutate({ lineId: r.id, ...next })}
                                />
                            )}
                        </div>
                    ),
            },
            {
                key: "asset_id",
                header: "Device",
                className: "w-56",
                render: (r) =>
                    editingLine?.id === r.id ? (
                        estSiteId ? (
                            <div
                                className="flex items-center gap-1"
                                title={
                                    deviceScope.source === "job"
                                        ? `Devices at ${deviceScope.inheritedSiteName ?? "the job's location"} — inherited from the originating job`
                                        : undefined
                                }
                            >
                                <DeviceBox
                                    devices={lineDevices}
                                    assetId={editingLine.asset_id ?? null}
                                    wholeJob={!!editingLine.whole_job}
                                    fallbackLabel={deviceLabel(r)}
                                    onChange={(n) =>
                                        setEditingLine((cur) =>
                                            cur ? { ...cur, asset_id: n.assetId, whole_job: n.wholeJob } : cur,
                                        )
                                    }
                                />
                                <button
                                    type="button"
                                    className="p-1 text-foreground/70 hover:text-primary shrink-0"
                                    title="Add a new device to this site and link it to this line"
                                    onClick={() => setAddDeviceForLine(r.id)}
                                >
                                    <PackagePlus className="h-3.5 w-3.5" />
                                </button>
                            </div>
                        ) : (
                            // No site anywhere — the estimate has none and it
                            // came from no job. Point at the fix instead of
                            // naming a step the app doesn't offer (Issue #1024).
                            <button
                                type="button"
                                className="text-xs text-primary underline underline-offset-2 text-left"
                                onClick={() =>
                                    setHeaderEdit({
                                        title: est.title ?? "",
                                        notes: est.notes ?? "",
                                        site_id: est.site_id ?? "",
                                        external_ref: est.external_ref ?? "",
                                        customer_po: est.customer_po ?? "",
                                        tax_rate: "",
                                        payment_terms: "",
                                    })
                                }
                            >
                                Set a location in Edit Details to link a device
                            </button>
                        )
                    ) : r.asset_id ? (
                        <span className="text-xs text-foreground/80">{deviceLabel(r)}</span>
                    ) : r.whole_job ? (
                        <span className="text-xs text-foreground/80">Whole job</span>
                    ) : (
                        <span className="text-muted-foreground">—</span>
                    ),
            },
            {
                key: "qty",
                header: "Qty",
                className: "w-24 text-right",
                render: (r) =>
                    editingLine?.id === r.id ? (
                        <Input
                            type="number"
                            className="h-7 text-sm w-16 text-right"
                            value={editingLine.qty}
                            onChange={(e) => setEditingLine({ ...editingLine, qty: e.target.value })}
                        />
                    ) : (
                        <span>{formatQty(r.qty)}</span>
                    ),
            },
            {
                key: "unit_price",
                header: "Unit Price",
                className: "w-28 text-right",
                render: (r) =>
                    editingLine?.id === r.id ? (
                        <Input
                            type="number"
                            step="0.01"
                            className="h-7 text-sm w-24 text-right"
                            value={editingLine.unit_price}
                            onChange={(e) => setEditingLine({ ...editingLine, unit_price: e.target.value })}
                        />
                    ) : (
                        formatDollars(Number(r.unit_price))
                    ),
            },
            {
                key: "line_total",
                header: "Total",
                className: "w-28 text-right",
                render: (r) => <span className="font-medium">{formatDollars(Number(r.line_total))}</span>,
            },
            {
                key: "taxed" as keyof EstimateLine,
                header: "Tax",
                className: "w-24 text-center",
                render: (r: EstimateLine) => {
                    return (
                        <TaxCell
                            line={r}
                            editable={canEditLines}
                            onToggle={(next) => patchEstimateLine.mutate({ lineId: r.id, taxable: next })}
                        />
                    );
                },
            },
            ...(canEditLines
                ? [
                      {
                          key: "actions" as keyof EstimateLine,
                          header: "",
                          className: "w-20",
                          render: (r: EstimateLine) =>
                              editingLine?.id === r.id ? (
                                  <div className="flex gap-1">
                                      <button
                                          className="p-1 hover:text-green-600"
                                          onClick={() =>
                                              patchEstimateLine.mutate({
                                                  lineId: r.id,
                                                  // Only changed words are sent, so an untouched
                                                  // line never becomes "typed by hand".
                                                  ...(editingLine.html !== undefined &&
                                                  editingLine.html !== editingLine.htmlStart
                                                      ? { description_html: editingLine.html }
                                                      : {}),
                                                  qty: Number(editingLine.qty),
                                                  unit_price: Number(editingLine.unit_price),
                                                  assetId: editingLine.asset_id ?? null,
                                                  ...(!editingLine.asset_id && !!editingLine.whole_job !== !!r.whole_job
                                                      ? { whole_job: !!editingLine.whole_job }
                                                      : {}),
                                              })
                                          }
                                      >
                                          <Check className="h-3.5 w-3.5" />
                                      </button>
                                      <button
                                          className="p-1 hover:text-foreground/80"
                                          onClick={() => setEditingLine(null)}
                                      >
                                          <X className="h-3.5 w-3.5" />
                                      </button>
                                  </div>
                              ) : (
                                  <div className="flex gap-1">
                                      <button
                                          className="p-1 hover:text-primary"
                                          onClick={() => {
                                              const start = initialRichHtml(r.description_html, r.description);
                                              setEditingLine({
                                                  id: r.id,
                                                  description: r.description,
                                                  qty: r.qty,
                                                  unit_price: r.unit_price,
                                                  asset_id: r.asset_id ?? null,
                                                  html: start,
                                                  htmlStart: start,
                                                  whole_job: !!r.whole_job,
                                              });
                                          }}
                                      >
                                          <Pencil className="h-3.5 w-3.5" />
                                      </button>
                                      <button
                                          className="p-1 hover:text-destructive"
                                          title={removesInOneTap(r) ? "Remove (Undo puts it back)" : "Remove"}
                                          onClick={async () => {
                                              // A line GWC added comes off in one tap; Undo puts it back.
                                              if (removesInOneTap(r)) {
                                                  estimateBulk.removeOne(r.id);
                                                  return;
                                              }
                                              if (
                                                  await confirmDialog({
                                                      message: "Remove this line?",
                                                      destructive: true,
                                                      confirmLabel: "Remove",
                                                  })
                                              )
                                                  deleteEstimateLine.mutate(r.id);
                                          }}
                                      >
                                          <Trash2 className="h-3.5 w-3.5" />
                                      </button>
                                  </div>
                              ),
                      },
                  ]
                : []),
        ];

        return (
            <div className="space-y-6">
                <div className="flex items-start justify-between">
                    <div>
                        <button
                            onClick={() => navigate(-1)}
                            className="text-sm text-shell-foreground/80 hover:text-shell-foreground inline-flex items-center gap-1 mb-2"
                        >
                            <ArrowLeft className="h-3 w-3" /> Back
                        </button>
                        <h1 className="text-2xl font-black tracking-tight text-shell-foreground">
                            Estimate <span className="font-mono">#{est.display_number || est.id.slice(0, 8)}</span>
                            {est.title && <span className="font-normal text-shell-foreground/80"> — {est.title}</span>}
                        </h1>
                        <div className="flex items-center gap-3 mt-2 flex-wrap">
                            <Badge className={statusColor(est.status)} variant="outline">
                                {commercialDocStatusLabel(est.status)}
                            </Badge>
                            <Badge variant="outline">Estimate</Badge>
                            {est.status === "declined" && est.declined_at && (
                                <span className="inline-flex items-center gap-1 rounded-full border border-red-500/40 bg-red-50 px-2 py-0.5 text-xs font-semibold text-red-700 dark:bg-red-950/40 dark:text-red-300">
                                    Declined{est.declined_by_name ? ` by ${est.declined_by_name}` : ""} ·{" "}
                                    {formatDate(est.declined_at)}
                                    {declineReasonLabel(est.decline_reason_code)
                                        ? ` · ${declineReasonLabel(est.decline_reason_code)}`
                                        : ""}
                                </span>
                            )}
                            {est.status === "accepted" &&
                                !est.st_estimate_id &&
                                (est.approved_via_token_at ? (
                                    <span className="inline-flex items-center gap-1 rounded-full border border-emerald-500/40 bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300">
                                        <CheckCircle2 className="h-3 w-3" />
                                        Approved online{est.approved_by_name
                                            ? ` by ${est.approved_by_name}`
                                            : ""} · {formatDate(est.approved_via_token_at)}
                                    </span>
                                ) : (
                                    <span className="inline-flex items-center rounded-full border px-2 py-0.5 text-xs text-muted-foreground">
                                        Marked accepted in the office
                                    </span>
                                ))}
                            {est.job_id && est.source_job_title && (
                                <button
                                    onClick={() => navigate(`/jobs/${est.job_id}`)}
                                    className="text-xs text-shell-foreground underline decoration-shell-foreground/45 underline-offset-2 hover:decoration-shell-foreground inline-flex items-center gap-1"
                                >
                                    <Briefcase className="h-3 w-3" />
                                    Original Job: {est.source_job_title}
                                </button>
                            )}
                            {est.followup_job_id && (
                                <button
                                    onClick={() => navigate(`/jobs/${est.followup_job_id}`)}
                                    className="text-xs text-shell-foreground underline decoration-shell-foreground/45 underline-offset-2 hover:decoration-shell-foreground inline-flex items-center gap-1"
                                >
                                    <ExternalLink className="h-3 w-3" />
                                    Linked Job: {est.followup_job_title || `#${est.followup_job_id.slice(0, 8)}`}
                                </button>
                            )}
                        </div>
                        {/* Second look (10/28): approval at a glance, the editing
                            warning on an approved quote, and Second look ✓. */}
                        <QuoteApprovalStrip estimateId={est.id} />
                    </div>
                    <div className="flex flex-wrap gap-2 justify-end">
                        <TaskCreateButton
                            prefill={{
                                title: `Estimate #${est.display_number || est.id.slice(0, 8)} — `,
                                links: [
                                    {
                                        entityType: "estimate",
                                        entityId: est.id,
                                        label: `Estimate #${est.display_number || est.id.slice(0, 8)}`,
                                    },
                                ],
                            }}
                        />
                        {(est.status === "draft" || est.status === "sent") && (
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() =>
                                    setHeaderEdit({
                                        title: est.title ?? "",
                                        notes: est.notes ?? "",
                                        site_id: est.site_id ?? "",
                                        external_ref: est.external_ref ?? "",
                                        customer_po: est.customer_po ?? "",
                                        tax_rate: "",
                                        payment_terms: "",
                                    })
                                }
                            >
                                <Settings className="h-4 w-4 mr-1" />
                                Edit Details
                            </Button>
                        )}
                        {/* ST → GW translator: imported ST estimates get the
                            full semantic translation preview (pricebook lines,
                            device refs, GW title/scope, tax). */}
                        {est.st_estimate_id && (
                            <Button
                                size="sm"
                                variant={est.translated_at ? "outline" : "default"}
                                onClick={() => navigate(`/commercial/${est.id}/translate`)}
                                title={
                                    est.translated_at
                                        ? "Already translated — re-run the preview"
                                        : "Resolve every ST line to native pricebook items before sending"
                                }
                            >
                                <Languages className="h-4 w-4 mr-1" />
                                {est.translated_at ? "Re-translate" : "Translate to Groundwork"}
                            </Button>
                        )}
                        <Button size="sm" variant="outline" onClick={() => setPreviewOpen(true)}>
                            <Printer className="h-4 w-4 mr-1" />
                            Preview
                        </Button>
                        {(est.status === "draft" || est.status === "sent") && (
                            <Button size="sm" onClick={requestSend}>
                                <Send className="h-4 w-4 mr-1" />
                                {est.status === "draft" ? "Send" : "Resend"}
                            </Button>
                        )}
                        {/* Issue #1623: was `sent`-only and recorded nothing
                            about how. A draft the customer already answered —
                            by mailing a work order, typically — had no path to
                            accepted at all, so it was left live and the later
                            WO scan made a second job. */}
                        {(est.status === "sent" || est.status === "draft") && (
                            <Button size="sm" onClick={() => setRecordApprovalOpen(true)}>
                                <CheckCircle className="h-4 w-4 mr-1" />
                                {est.status === "draft" ? "Mark approved" : "Approve"}
                            </Button>
                        )}
                        {/*
                         * Per PRE-CUTOVER-UX-FEEDBACK.md #35: after the
                         * follow-up job exists, the green "Create Job" CTA
                         * flips to "View Job" so the office never re-creates
                         * a duplicate. The detail card below also surfaces
                         * the originating job summary (#32) for context.
                         */}
                        {est.status === "accepted" && !est.followup_job_id && (
                            <Button
                                size="sm"
                                className="bg-green-700 hover:bg-green-800 text-white"
                                onClick={() => createFollowUpJob.mutate()}
                                disabled={createFollowUpJob.isPending}
                            >
                                {createFollowUpJob.isPending ? (
                                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                                ) : (
                                    <Briefcase className="h-4 w-4 mr-1" />
                                )}
                                Create Job
                            </Button>
                        )}
                        {est.followup_job_id && (
                            <Button
                                size="sm"
                                className="bg-green-700 hover:bg-green-800 text-white"
                                onClick={() => navigate(`/jobs/${est.followup_job_id}`)}
                            >
                                <ExternalLink className="h-4 w-4 mr-1" />
                                View Job
                            </Button>
                        )}
                        {(est.job_id || est.followup_job_id) && (
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => createPo.mutate()}
                                disabled={createPo.isPending}
                                title="Draft a purchase order from this estimate's material lines (idempotent — never duplicates)"
                            >
                                {createPo.isPending ? (
                                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                                ) : (
                                    <PackagePlus className="h-4 w-4 mr-1" />
                                )}
                                Create PO from estimate
                            </Button>
                        )}
                        {est.status === "accepted" && (
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => convertToInvoice.mutate()}
                                disabled={convertToInvoice.isPending}
                                title="Bill this estimate directly — creates an invoice without a separate job"
                            >
                                {convertToInvoice.isPending ? (
                                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                                ) : (
                                    <Receipt className="h-4 w-4 mr-1" />
                                )}
                                Convert to Invoice
                            </Button>
                        )}
                        {/* Reopen-to-correct (Issue #570): the governed door
                            back to an editable draft when an issued estimate has
                            a mistake — admin/finance only, reason required, the
                            approved version snapshotted and the reopen logged. */}
                        {canReopen && (
                            <Button
                                size="sm"
                                variant="outline"
                                className="text-amber-700 dark:text-amber-400"
                                onClick={() => setReopenOpen(true)}
                                title={
                                    est.status === "declined"
                                        ? "Take another run at it — drops back to an editable draft so you can change the numbers and re-send."
                                        : "Made a mistake? Reopen this estimate to an editable draft — the approved version is snapshotted and the reopen is logged."
                                }
                            >
                                <RotateCcw className="h-4 w-4 mr-1" />
                                {est.status === "declined" ? "Re-quote" : "Reopen to correct"}
                            </Button>
                        )}
                        {/* EST-1: dead quotes need a clean exit that keeps
                            history — hidden once accepted or already cancelled,
                            except an accepted quote whose job was cancelled
                            (#1743): its work will never happen, and the server
                            lets it close without a Reopen. */}
                        {est.status !== "cancelled" &&
                            (est.status !== "accepted" || est.followup_job_status === "cancelled") && (
                                <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-red-600 dark:text-red-400"
                                    onClick={() => setCancelQuoteOpen(true)}
                                >
                                    <Ban className="h-4 w-4 mr-1" />
                                    Cancel Quote
                                </Button>
                            )}
                    </div>
                </div>

                {sendGuardOpen && untagged.length > 0 && (
                    <SendGuard
                        count={untagged.length}
                        onDismiss={() => setSendGuardOpen(false)}
                        onSendAnyway={() => {
                            setSendGuardOpen(false);
                            setSendOpen(true);
                        }}
                        onTagThem={() => {
                            setSendGuardOpen(false);
                            if (!groupByDevice) {
                                setGroupByDevice(true);
                                writeGroupPref(true);
                            }
                            estimateBulk.clear();
                            for (const l of untagged) estimateBulk.toggle(l.id);
                            setTagMenuOpen(true);
                            document
                                .querySelector('[data-section="none"]')
                                ?.scrollIntoView({ behavior: "smooth", block: "center" });
                        }}
                    />
                )}

                {/* Post-send edit nudge (Issue #753): a sent estimate stays
                    editable, but every content edit stales the copy already in
                    the customer's inbox — say so until a resend goes out. */}
                {est.status === "sent" && est.edited_since_sent && (
                    <div className="flex flex-wrap items-center gap-3 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
                        <AlertTriangle className="h-4 w-4 shrink-0" />
                        <span className="flex-1">
                            Edited since last sent
                            {est.last_sent_at ? ` (${formatDate(est.last_sent_at)})` : ""} — the customer&apos;s copy
                            shows the old numbers. Resend so they see current numbers.
                        </span>
                        <Button size="sm" onClick={requestSend}>
                            <Send className="h-4 w-4 mr-1" />
                            Resend
                        </Button>
                    </div>
                )}

                <div className="grid gap-4 md:grid-cols-3">
                    <Card>
                        <CardHeader className="pb-3">
                            <CardTitle className="text-sm flex items-center gap-2">
                                <FileText className="h-4 w-4" /> Details
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="text-sm space-y-1">
                            {/* Who and where. Neither was on this card before —
                                the estimate knew its site and the page still
                                couldn't show it, because the response returned
                                no site name (Issue #1024 follow-up). */}
                            {est.customer_name && (
                                <p>
                                    <span className="text-foreground/80">Customer:</span>{" "}
                                    {est.customer_id ? (
                                        <Link
                                            to={`/customers/${est.customer_id}`}
                                            className="font-medium text-primary hover:underline"
                                        >
                                            {est.customer_name}
                                        </Link>
                                    ) : (
                                        <span className="font-medium">{est.customer_name}</span>
                                    )}
                                    <BudgetChip
                                        customerId={est.customer_id}
                                        category={quoteCategory(est.title)}
                                        className="ml-2 align-middle"
                                    />
                                </p>
                            )}
                            <p className="flex flex-wrap items-baseline gap-x-1">
                                <span className="text-foreground/80">Location:</span>{" "}
                                {estSiteId ? (
                                    <>
                                        <Link
                                            to={`/sites/${estSiteId}`}
                                            className="font-medium text-primary hover:underline"
                                        >
                                            {deviceScope.source === "estimate"
                                                ? [est.site_name, est.site_city].filter(Boolean).join(" — ") ||
                                                  "This location"
                                                : (deviceScope.inheritedSiteName ?? "The job's location")}
                                        </Link>
                                        {deviceScope.source === "job" && (
                                            <span className="text-xs text-foreground/60">(from the job)</span>
                                        )}
                                    </>
                                ) : (
                                    <button
                                        type="button"
                                        className="font-medium text-primary underline underline-offset-2"
                                        onClick={() =>
                                            setHeaderEdit({
                                                title: est.title ?? "",
                                                notes: est.notes ?? "",
                                                site_id: est.site_id ?? "",
                                                external_ref: est.external_ref ?? "",
                                                customer_po: est.customer_po ?? "",
                                                tax_rate: "",
                                                payment_terms: "",
                                            })
                                        }
                                    >
                                        None — set one
                                    </button>
                                )}
                            </p>
                            <p>
                                <span className="text-foreground/80">Created:</span> {formatDate(est.created_at)}
                            </p>
                            {est.status === "accepted" && !est.st_estimate_id && (
                                <p>
                                    <span className="text-foreground/80">Approved:</span>{" "}
                                    {est.approved_via_token_at ? (
                                        <span className="font-medium text-emerald-700 dark:text-emerald-400">
                                            online{est.approved_by_name ? ` by ${est.approved_by_name}` : ""} on{" "}
                                            {formatDate(est.approved_via_token_at)}
                                        </span>
                                    ) : (
                                        "marked accepted in the office"
                                    )}
                                </p>
                            )}
                        </CardContent>
                    </Card>
                    <Card>
                        <CardHeader className="pb-3">
                            <CardTitle className="text-sm flex items-center gap-2">
                                <DollarSign className="h-4 w-4" /> Total
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <p className="text-2xl font-bold">
                                {formatDollars(Number(est.subtotal) + Number(est.tax_amount ?? 0))}
                            </p>
                            <p className="text-xs text-foreground/80">
                                Subtotal: {formatDollars(Number(est.subtotal))}
                            </p>
                            {/* #1625 — the number above is the SOLD total on an
                                optioned quote, and nothing used to say so. */}
                            {est.status === "accepted" && soldSummary && (
                                <p className="text-xs font-medium text-emerald-700 dark:text-emerald-400">
                                    Sold — {soldSummary.deviceCount} device
                                    {soldSummary.deviceCount === 1 ? "" : "s"}
                                    {soldSummary.label ? `, ${soldSummary.label}` : ""}
                                    {soldSummary.declined > 0 ? ` · ${soldSummary.declined} declined` : ""}
                                </p>
                            )}
                            <TaxTreatmentPanel
                                docType="estimate"
                                docId={est.id}
                                customerId={est.customer_id ?? null}
                                doc={est}
                                editable={est.status === "draft"}
                                onSaved={() => qc.invalidateQueries({ queryKey: ["estimate", id] })}
                            />
                        </CardContent>
                    </Card>
                    {est.job_id && (
                        <OriginatingJobSummaryCard
                            jobId={est.job_id}
                            title={est.source_job_title ?? null}
                            displayNumber={est.source_job_display_number ?? null}
                            status={est.source_job_status ?? null}
                            scheduledStart={est.source_job_scheduled_start ?? null}
                            techName={est.source_job_tech_name ?? null}
                            onNavigate={() => navigate(`/jobs/${est.job_id}`)}
                        />
                    )}
                </div>

                {/* The two fields the office rewrites on nearly every quote, in
                    place. The title lived only in the page header and the scope
                    text (`notes`) rendered as a card BELOW the line items and
                    the deposit panel — and not at all when empty, so a blank
                    scope could only be filled from the Edit Details modal.
                    Both ride the customer's PDF (SCOPE OF WORK) and email, so
                    they belong above the money, editable without leaving. */}
                <CustomerFacingTextCard
                    title={est.title ?? ""}
                    notes={est.notes ?? ""}
                    editable={est.status === "draft" || est.status === "sent"}
                    saving={saveEstimateHeader.isPending}
                    onSave={(next) => saveEstimateHeader.mutate(next)}
                />

                {(est.status === "draft" || est.status === "sent") && <EstimateReviewPanel estimateId={est.id} />}

                {/* #1625 — the customer's answer, before the flat line table.
                    An estimate with no options renders nothing here and the
                    screen is byte-for-byte what it always was. The mismatch
                    banner leads, because an invoice billing work nobody bought
                    outranks everything else on the page. */}
                {estimateData?.invoiceMismatch && <InvoiceMismatchBanner mismatch={estimateData.invoiceMismatch} />}
                {estimateData?.optionView && (
                    <EstimateOptionGroups
                        view={estimateData.optionView}
                        lines={estimateLinesRaw}
                        status={est.status}
                        soldTotal={Number(est.subtotal)}
                        removal={
                            canEditLines
                                ? {
                                      onRemove: optionRemoval.removeOne,
                                      onDropEverywhere: optionRemoval.dropEverywhere,
                                      pending: optionRemoval.pending,
                                  }
                                : undefined
                        }
                    />
                )}

                {/* Device check (11/2): the tag photo next to the parts, one
                    panel per device the quote has lines for. Parts are
                    ordered from the record — hold it up to the tag here. */}
                {quoteDeviceIds.length > 0 && (
                    <div className="space-y-2" data-testid="quote-devices">
                        <h2 className="text-sm font-semibold text-shell-foreground">
                            Check each device against its tag — parts are picked from this record
                        </h2>
                        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                            {quoteDeviceIds.map((assetId) => {
                                const d = siteDevices.find((x) => x.id === assetId);
                                return (
                                    <TagPhotoPanel
                                        key={assetId}
                                        assetId={assetId}
                                        record={{
                                            name: d?.name,
                                            manufacturer: d?.manufacturer,
                                            model: d?.model,
                                            size_label: d?.size_label,
                                            assembly_type: d?.assembly_type,
                                            serial_number: d?.serial_number,
                                            external_source_key: d?.external_key,
                                        }}
                                        compact
                                    />
                                );
                            })}
                        </div>
                    </div>
                )}

                <div className="space-y-3">
                    {/* Which card this quote follows (#1823 S5). */}
                    <RateCardBadge customerId={est.customer_id} />
                    <div className="flex flex-wrap items-center justify-between gap-2">
                        <h2 className="text-lg font-semibold text-shell-foreground">
                            {hasOptionGroups ? "All line items" : "Line Items"}
                        </h2>
                        <div className="flex flex-wrap items-center gap-2">
                            {/* Decision 4: grouped by device, Device column hidden.
                                Off brings back today's flat list. */}
                            <button
                                type="button"
                                aria-pressed={groupByDevice}
                                data-testid="group-by-device"
                                className="inline-flex h-9 items-center gap-2 rounded-[10px] bg-card px-2.5 text-[12.5px] font-semibold text-foreground shadow-card"
                                onClick={() => {
                                    const next = !groupByDevice;
                                    setGroupByDevice(next);
                                    writeGroupPref(next);
                                    setGroupedEditId(null);
                                    setEditingLine(null);
                                }}
                            >
                                <span
                                    className={`relative h-4 w-7 shrink-0 rounded-full transition-colors ${groupByDevice ? "bg-primary" : "bg-muted-foreground/50"}`}
                                >
                                    <span
                                        className={`absolute top-0.5 h-3 w-3 rounded-full bg-white transition-[left] ${groupByDevice ? "left-[14px]" : "left-0.5"}`}
                                    />
                                </span>
                                Group by device
                            </button>
                            {canEditLines && (
                                <>
                                    {/* #1441 — the rule, as a button. It groups each
                                        device's work and lifts labor above the
                                        materials; it never runs on its own, and it
                                        leaves anything it cannot classify where it
                                        is. Disabled when there is nothing to do, so
                                        pressing it is never a no-op that looks like
                                        a failure. */}
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        disabled={!needsTidying(estimateReorder.ordered) || estimateReorder.saving}
                                        title={
                                            needsTidying(estimateReorder.ordered)
                                                ? "Put the lines in device order (labor first) — the order the PDF prints"
                                                : "Already in order"
                                        }
                                        onClick={() =>
                                            estimateReorder.apply(
                                                tidyLineOrder(estimateReorder.ordered).map((l) => l.id),
                                            )
                                        }
                                    >
                                        <ArrowUpDown className="h-4 w-4 mr-1" />
                                        Tidy up
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => addCustomEstimateLine.mutate()}
                                        disabled={addCustomEstimateLine.isPending}
                                    >
                                        {addCustomEstimateLine.isPending ? (
                                            <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                                        ) : (
                                            <Plus className="h-4 w-4 mr-1" />
                                        )}
                                        Add custom line
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => addLaborEstimateLine.mutate()}
                                        disabled={addLaborEstimateLine.isPending}
                                        title="Adds a labor line at this customer's rate — then set hours and techs"
                                    >
                                        {addLaborEstimateLine.isPending ? (
                                            <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                                        ) : (
                                            <Plus className="h-4 w-4 mr-1" />
                                        )}
                                        Add labor
                                    </Button>
                                    {est.status === "draft" && (
                                        <RepriceFromCardButton
                                            estimateId={est.id}
                                            onApplied={() => qc.invalidateQueries({ queryKey: ["estimate", id] })}
                                        />
                                    )}
                                    {siteDevices.length > 0 && (
                                        <DeviceMenu
                                            devices={lineDevices}
                                            mode="repair"
                                            align="end"
                                            open={repairMenuOpen}
                                            onOpenChange={setRepairMenuOpen}
                                            onPick={(p) => {
                                                if (p.kind === "device")
                                                    addRepairForDevice.mutate({ assetId: p.device.id, kind: p.work });
                                            }}
                                            trigger={
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    aria-label="Add repair for device"
                                                    disabled={addRepairForDevice.isPending}
                                                >
                                                    {addRepairForDevice.isPending ? (
                                                        <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                                                    ) : (
                                                        <Wrench className="h-4 w-4 mr-1" />
                                                    )}
                                                    Add repair for device
                                                    <ChevronDown className="h-4 w-4 ml-1 opacity-60" />
                                                </Button>
                                            }
                                        />
                                    )}
                                    <PricebookLinePicker
                                        estimateId={est.id}
                                        devices={siteDevices.map((d) => ({ id: d.id, label: deviceOptionLabel(d) }))}
                                    />
                                </>
                            )}
                        </div>
                    </div>
                    {groupByDevice ? (
                        <>
                            {estimateLinesRaw.length === 0 ? (
                                <div className="rounded-[14px] bg-card px-4 py-6 text-center text-sm text-muted-foreground shadow-card">
                                    No line items yet — “Add repair for device”, “Add from pricebook” for catalog work,
                                    or “Add custom line” to type your own.
                                </div>
                            ) : (
                                <GroupedLineList
                                    lines={estimateReorder.ordered}
                                    devices={lineDevices}
                                    canEdit={canEditLines}
                                    selected={estimateBulk.selected}
                                    onToggle={estimateBulk.toggle}
                                    onTag={(lineIds, pick) => tagLinesMut.mutate({ lineIds, pick })}
                                    onMove={async (lineId, order, pick) => {
                                        // Another group: tag it there first, then place it.
                                        if (pick) {
                                            try {
                                                await tagLinesMut.mutateAsync({ lineIds: [lineId], pick });
                                            } catch {
                                                return; // tagLinesMut already said why
                                            }
                                        }
                                        estimateReorder.apply(order);
                                    }}
                                    onSplit={(l, assetIds) => splitLineMut.mutate({ lineId: l.id, assetIds })}
                                    onReword={(l) => rewordMut.mutate(l.id)}
                                    onUndo={(l) => undoRewordMut.mutate(l.id)}
                                    onEdit={(l) => setGroupedEditId(l.id)}
                                    onDelete={async (l) => {
                                        // A line GWC added comes off in one tap; Undo puts it back.
                                        if (removesInOneTap(l)) {
                                            estimateBulk.removeOne(l.id);
                                            return;
                                        }
                                        if (
                                            await confirmDialog({
                                                message: "Remove this line?",
                                                destructive: true,
                                                confirmLabel: "Remove",
                                            })
                                        )
                                            deleteEstimateLine.mutate(l.id);
                                    }}
                                    editingId={groupedEditId}
                                    renderEditor={(l) => (
                                        <LineEditorPanel
                                            line={l}
                                            devices={lineDevices}
                                            pending={patchEstimateLine.isPending}
                                            onCancel={() => setGroupedEditId(null)}
                                            onSave={(body: LineEditorSave) =>
                                                patchEstimateLine.mutate(
                                                    { lineId: l.id, ...body },
                                                    { onSuccess: () => setGroupedEditId(null) },
                                                )
                                            }
                                        />
                                    )}
                                    renderLabor={(l) =>
                                        canEditLines && laborOf(l) ? (
                                            <LaborCrewStepper
                                                labor={laborOf(l)!}
                                                pending={
                                                    patchEstimateLine.isPending &&
                                                    patchEstimateLine.variables?.lineId === l.id
                                                }
                                                onChange={(next) => patchEstimateLine.mutate({ lineId: l.id, ...next })}
                                            />
                                        ) : null
                                    }
                                    renderTax={(l) => {
                                        return (
                                            <TaxCell
                                                line={l}
                                                editable={canEditLines}
                                                onToggle={(next) =>
                                                    patchEstimateLine.mutate({ lineId: l.id, taxable: next })
                                                }
                                            />
                                        );
                                    }}
                                    previews={piecePreviews}
                                    onAddMissing={(d, piece) =>
                                        addMissingMut.mutate({ assetId: d.id, piece, kind: workKindFor(d.id) })
                                    }
                                    addingPiece={
                                        addMissingMut.isPending && addMissingMut.variables
                                            ? {
                                                  deviceId: addMissingMut.variables.assetId,
                                                  piece: addMissingMut.variables.piece,
                                              }
                                            : null
                                    }
                                    onAddLine={(d) => addLineToDevice.mutate(d.id)}
                                    chips={lineChips}
                                    busyLineIds={
                                        new Set(
                                            [
                                                rewordMut.isPending ? rewordMut.variables : null,
                                                undoRewordMut.isPending ? undoRewordMut.variables : null,
                                                splitLineMut.isPending ? splitLineMut.variables?.lineId : null,
                                                ...(tagLinesMut.isPending
                                                    ? (tagLinesMut.variables?.lineIds ?? [])
                                                    : []),
                                            ].filter((x): x is string => !!x),
                                        )
                                    }
                                />
                            )}
                            {canEditLines && (
                                <SelectionBar
                                    count={estimateBulk.selected.size}
                                    total={estimateBulk.total}
                                    onSelectAll={estimateBulk.selectAll}
                                    devices={lineDevices}
                                    tagOpen={tagMenuOpen}
                                    onTagOpenChange={setTagMenuOpen}
                                    onTag={(pick) => tagLinesMut.mutate({ lineIds: [...estimateBulk.selected], pick })}
                                    onSplit={
                                        splittableSelected.length > 0
                                            ? () => {
                                                  for (const l of splittableSelected)
                                                      splitLineMut.mutate({ lineId: l.id, assetIds: quoteDeviceIds });
                                              }
                                            : null
                                    }
                                    onDelete={estimateBulk.deleteSelected}
                                    pending={estimateBulk.pending || tagLinesMut.isPending || splitLineMut.isPending}
                                    onClear={() => {
                                        estimateBulk.clear();
                                        setTagMenuOpen(false);
                                    }}
                                />
                            )}
                        </>
                    ) : (
                        <>
                            <DataTable
                                columns={lineCols}
                                data={estimateReorder.ordered}
                                keyField="id"
                                rowProps={estimateReorder.rowProps}
                                emptyMessage="No line items yet — “Add from pricebook” for catalog work, or “Add custom line” to type your own."
                            />
                            {estimateLinesRaw.some((l) => lineOrigin(l) !== null) && <LineOriginKey />}
                            <BulkDeleteBar bulk={estimateBulk} />
                        </>
                    )}
                </div>

                <EstimateDepositPanel estimateId={est.id} />

                {/* The old read-only "Notes" card lived here, at the very
                    bottom. It is now CustomerFacingTextCard above the line
                    items, where the scope text can actually be read and fixed. */}

                <CancelEstimateDialog
                    open={cancelQuoteOpen}
                    onClose={() => setCancelQuoteOpen(false)}
                    estimateId={est.id}
                    estimateLabel={`Estimate #${est.display_number || est.id.slice(0, 8)}`}
                    existingNotes={est.notes}
                />

                <RecordApprovalDialog
                    open={recordApprovalOpen}
                    onClose={() => setRecordApprovalOpen(false)}
                    estimateId={est.id}
                    estimateLabel={`Estimate #${est.display_number || est.id.slice(0, 8)}`}
                    defaultReference={est.external_ref ?? null}
                />

                <ReopenEstimateDialog
                    open={reopenOpen}
                    onClose={() => setReopenOpen(false)}
                    estimateId={est.id}
                    estimateLabel={`Estimate #${est.display_number || est.id.slice(0, 8)}`}
                    approvedOnline={!!est.approved_via_token_at}
                    priorStatus={est.status}
                    onReopened={() => setReopenOpen(false)}
                />

                {headerEdit && (
                    <Dialog open={true} onClose={() => setHeaderEdit(null)} title="Edit Estimate Details">
                        <div className="space-y-3">
                            <div>
                                <Label>Title</Label>
                                <Input
                                    value={headerEdit.title}
                                    onChange={(e) => setHeaderEdit({ ...headerEdit, title: e.target.value })}
                                />
                            </div>
                            <div>
                                <Label>Location</Label>
                                <Select
                                    value={headerEdit.site_id ?? ""}
                                    onChange={(e) => setHeaderEdit({ ...headerEdit, site_id: e.target.value })}
                                >
                                    <option value="">None</option>
                                    {customerSites.map((s) => (
                                        <option key={s.id} value={s.id}>
                                            {s.city ? `${s.name} — ${s.city}` : s.name}
                                        </option>
                                    ))}
                                </Select>
                                <p className="text-xs text-foreground/60 mt-1">
                                    {deviceScope.source === "job" && !est.site_id
                                        ? `Lines currently read devices from the originating job's location${deviceScope.inheritedSiteName ? ` (${deviceScope.inheritedSiteName})` : ""}. Setting it here makes that permanent.`
                                        : "Devices live on locations — a line can only link one once this is set."}
                                </p>
                            </div>
                            {/* Issue #1623 — recordable at ANY status, unlike
                                everything else on this quote. A vendor work
                                order routinely arrives while the quote is still
                                draft or sent; refusing the number until the
                                quote is accepted is exactly what left intake
                                with nothing to match and made a second job. */}
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label>Work order / service order #</Label>
                                    <Input
                                        value={headerEdit.external_ref ?? ""}
                                        placeholder="S2604200992-3"
                                        onChange={(e) => setHeaderEdit({ ...headerEdit, external_ref: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <Label>Customer PO #</Label>
                                    <Input
                                        value={headerEdit.customer_po ?? ""}
                                        placeholder="4500231070"
                                        onChange={(e) => setHeaderEdit({ ...headerEdit, customer_po: e.target.value })}
                                    />
                                </div>
                                <p className="col-span-2 -mt-1 text-xs text-foreground/60">
                                    Put the vendor&apos;s number here as soon as it arrives. When that work order is
                                    scanned, it finds this quote — and the repair job made from it — instead of creating
                                    a second job.
                                </p>
                            </div>
                            <div>
                                <Label>Notes</Label>
                                <Textarea
                                    rows={3}
                                    value={headerEdit.notes}
                                    onChange={(e) => setHeaderEdit({ ...headerEdit, notes: e.target.value })}
                                />
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 mt-4">
                            <Button variant="outline" onClick={() => setHeaderEdit(null)}>
                                Cancel
                            </Button>
                            <Button
                                onClick={() =>
                                    saveEstimateHeader.mutate({
                                        title: headerEdit.title,
                                        notes: headerEdit.notes,
                                        // Only send a change — a no-op site
                                        // patch would needlessly re-validate.
                                        ...((headerEdit.site_id ?? "") !== (est.site_id ?? "")
                                            ? { siteId: headerEdit.site_id || null }
                                            : {}),
                                        ...((headerEdit.external_ref ?? "") !== (est.external_ref ?? "")
                                            ? { externalRef: headerEdit.external_ref || null }
                                            : {}),
                                        ...((headerEdit.customer_po ?? "") !== (est.customer_po ?? "")
                                            ? { customerPo: headerEdit.customer_po || null }
                                            : {}),
                                    })
                                }
                                disabled={saveEstimateHeader.isPending}
                            >
                                {saveEstimateHeader.isPending ? (
                                    <Loader2 className="h-4 w-4 animate-spin mr-1" />
                                ) : null}
                                Save
                            </Button>
                        </div>
                    </Dialog>
                )}

                <DocumentPreviewDialog
                    open={previewOpen}
                    onClose={() => setPreviewOpen(false)}
                    kind="estimate"
                    documentId={id ?? null}
                    label={est.display_number ?? est.title ?? null}
                    suggestedFilename={est.display_number ? `estimate-${est.display_number}.pdf` : null}
                />

                {id && (
                    <SendCustomerDialog
                        docType="estimate"
                        id={id}
                        customerId={est.customer_id ?? null}
                        open={sendOpen}
                        onOpenChange={setSendOpen}
                        onSent={() => qc.invalidateQueries({ queryKey: ["estimate", id] })}
                    />
                )}

                {addDeviceForLine && estSiteId && (
                    <AddDeviceToSiteModal
                        siteId={estSiteId}
                        onClose={() => setAddDeviceForLine(null)}
                        onCreated={(asset: CreatedAsset) => {
                            // Refresh the picker so the new device shows, and
                            // stage it on the line that's mid-edit (saved on Check).
                            void siteDevicesQuery.refetch();
                            if (editingLine && editingLine.id === addDeviceForLine) {
                                setEditingLine({ ...editingLine, asset_id: asset.id });
                            }
                        }}
                    />
                )}
            </div>
        );
    }

    const inv = invoiceData?.invoice;
    if (!inv)
        return (
            <DetailErrorState
                error={invoiceError}
                onRetry={() => void refetchInvoice()}
                notFoundTitle="Invoice not found"
                listHref="/commercial"
                listLabel="Back to documents"
            />
        );
    const payments = invoiceData?.payments ?? [];

    // Remaining balance = total minus payments already recorded (net of any
    // negative refund rows). Drives the default payment amount and which
    // payment buttons show.
    const totalPaid = payments.reduce((s, p) => s + Number(p.amount), 0);
    const remainingBalance = Math.max(0, Number(inv.total_amount) - totalPaid);

    // H16: validate the typed payment amount inline. Previously a 0/negative/
    // over-balance amount was silently dropped and the server defaulted to the
    // full remaining balance — so the office thought it recorded a partial when
    // it actually paid the invoice in full. An empty field still means "pay the
    // full remaining balance" (the intended default), but any typed value must
    // be positive and within the balance.
    const paymentAmountRaw = paymentForm.amount;
    const paymentAmountIsEmpty = paymentAmountRaw === "" || paymentAmountRaw === undefined || paymentAmountRaw === null;
    const paymentAmountNum = paymentAmountIsEmpty ? null : Number(paymentAmountRaw);
    const paymentAmountError: string | null = paymentAmountIsEmpty
        ? null
        : paymentAmountNum === null || !Number.isFinite(paymentAmountNum)
          ? "Enter a valid dollar amount."
          : paymentAmountNum <= 0
            ? "Amount must be greater than $0."
            : paymentAmountNum > remainingBalance + 0.005
              ? `Amount can't exceed the remaining balance of ${formatDollars(remainingBalance)}.`
              : null;

    // A Stripe refund only applies to an actual Stripe charge. Key off the
    // 'stripe' method, then net out prior refunds (negative 'stripe_refund'
    // rows) to get the still-refundable amount. Admin-only (server enforces).
    const stripeCharged = payments
        .filter((p) => p.method === "stripe" && Number(p.amount) > 0)
        .reduce((s, p) => s + Number(p.amount), 0);
    const stripeRefunded = payments
        .filter((p) => p.method === "stripe_refund")
        .reduce((s, p) => s + Math.abs(Number(p.amount)), 0);
    const refundable = Math.max(0, stripeCharged - stripeRefunded);
    const canRefund = isAdmin && refundable > 0;

    // ServiceTitan imports with a real total over $0 lines: the server refuses
    // every line edit (it would recompute the total from $0 lines), so hide them.
    const linesNotItemized = inv.lines_not_itemized === true;
    const canEditInvLines = !["paid", "void"].includes(inv.status) && !linesNotItemized;
    // #1231 — a paid invoice's figures are settled, but its wording has to stay
    // correctable: the backflow line quotes the device serial and the test
    // result, so a typo or a late-passing test otherwise stands on the
    // customer's copy forever. Text only — qty, price and tax stay read-only.
    const canCorrectLineText = inv.status === "paid" && !linesNotItemized;
    const showLineActions = canEditInvLines || canCorrectLineText;
    const lineCols: Column<InvoiceLine>[] = [
        ...(canEditInvLines
            ? [
                  {
                      key: "select" as keyof InvoiceLine & string,
                      header: "",
                      className: "w-8 pr-0",
                      render: (r: InvoiceLine) => <LineCheckbox bulk={invoiceBulk} id={r.id} label={r.description} />,
                  },
              ]
            : []),
        ...(canEditInvLines
            ? [
                  {
                      key: "reorder" as keyof InvoiceLine & string,
                      header: "",
                      className: "w-8 pr-0",
                      render: (r: InvoiceLine) => invoiceReorder.renderHandle(r),
                  },
              ]
            : []),
        {
            key: "description",
            header: "Description",
            render: (r) =>
                editingLine?.id === r.id ? (
                    <Input
                        className="h-7 text-sm"
                        value={editingLine.description}
                        onChange={(e) => setEditingLine({ ...editingLine, description: e.target.value })}
                    />
                ) : (
                    <div className="flex flex-col gap-1.5">
                        <span>{stripHtml(r.description)}</span>
                        <OfficeOnlyAssemblyNote note={r.office_assembly} />
                        <FeeAudiencePick line={r} doc="invoice" docId={id} canEdit={canEditInvLines} />
                        {canEditInvLines && laborOf(r) && (
                            <LaborCrewStepper
                                labor={laborOf(r)!}
                                pending={patchInvoiceLine.isPending && patchInvoiceLine.variables?.lineId === r.id}
                                onChange={(next) => patchInvoiceLine.mutate({ lineId: r.id, ...next })}
                            />
                        )}
                    </div>
                ),
        },
        {
            key: "qty",
            header: "Qty",
            className: "w-24 text-right",
            render: (r) =>
                editingLine?.id === r.id && canEditInvLines ? (
                    <Input
                        type="number"
                        className="h-7 text-sm w-16 text-right"
                        value={editingLine.qty}
                        onChange={(e) => setEditingLine({ ...editingLine, qty: e.target.value })}
                    />
                ) : (
                    <span>{r.qty}</span>
                ),
        },
        {
            key: "unit_price",
            header: "Unit Price",
            className: "w-28 text-right",
            render: (r) =>
                editingLine?.id === r.id && canEditInvLines ? (
                    <Input
                        type="number"
                        step="0.01"
                        className="h-7 text-sm w-24 text-right"
                        value={editingLine.unit_price}
                        onChange={(e) => setEditingLine({ ...editingLine, unit_price: e.target.value })}
                    />
                ) : (
                    formatDollars(Number(r.unit_price))
                ),
        },
        {
            key: "line_total",
            header: "Total",
            className: "w-28 text-right",
            render: (r) => <span className="font-medium">{formatDollars(Number(r.line_total))}</span>,
        },
        {
            key: "taxed" as keyof InvoiceLine,
            header: "Tax",
            className: "w-24 text-center",
            render: (r: InvoiceLine) => {
                return (
                    <TaxCell
                        line={r}
                        editable={canEditInvLines}
                        onToggle={(next) => patchInvoiceLine.mutate({ lineId: r.id, taxable: next })}
                    />
                );
            },
        },
        ...(showLineActions
            ? [
                  {
                      key: "actions" as keyof InvoiceLine,
                      header: "",
                      className: "w-20",
                      render: (r: InvoiceLine) =>
                          editingLine?.id === r.id ? (
                              <div className="flex gap-1">
                                  <button
                                      className="p-1 hover:text-green-600"
                                      onClick={() =>
                                          patchInvoiceLine.mutate(
                                              // On a paid invoice send the text and
                                              // nothing else — a money field in the
                                              // body is what the server refuses.
                                              canEditInvLines
                                                  ? {
                                                        lineId: r.id,
                                                        description: editingLine.description,
                                                        qty: Number(editingLine.qty),
                                                        unit_price: Number(editingLine.unit_price),
                                                    }
                                                  : { lineId: r.id, description: editingLine.description },
                                          )
                                      }
                                  >
                                      <Check className="h-3.5 w-3.5" />
                                  </button>
                                  <button className="p-1 hover:text-foreground/80" onClick={() => setEditingLine(null)}>
                                      <X className="h-3.5 w-3.5" />
                                  </button>
                              </div>
                          ) : (
                              <div className="flex gap-1">
                                  <button
                                      className="p-1 hover:text-primary"
                                      onClick={() =>
                                          setEditingLine({
                                              id: r.id,
                                              description: r.description,
                                              qty: r.qty,
                                              unit_price: r.unit_price,
                                          })
                                      }
                                  >
                                      <Pencil className="h-3.5 w-3.5" />
                                  </button>
                                  {canEditInvLines && (
                                      <button
                                          className="p-1 hover:text-destructive"
                                          onClick={async () => {
                                              if (
                                                  await confirmDialog({
                                                      message: "Remove this line?",
                                                      destructive: true,
                                                      confirmLabel: "Remove",
                                                  })
                                              )
                                                  deleteInvoiceLine.mutate(r.id);
                                          }}
                                      >
                                          <Trash2 className="h-3.5 w-3.5" />
                                      </button>
                                  )}
                              </div>
                          ),
                  },
              ]
            : []),
    ];

    const paymentCols: Column<PaymentRow>[] = [
        {
            key: "amount",
            header: "Amount",
            sortable: true,
            firstSortDir: "desc",
            sortAccessor: (r) => moneySortValue(r.amount),
            render: (r) => <span className="font-medium">{formatDollars(Number(r.amount))}</span>,
        },
        { key: "method", header: "Method", sortable: true },
        { key: "reference", header: "Reference", sortable: true },
        {
            key: "paid_at",
            header: "Date",
            sortable: true,
            firstSortDir: "desc",
            sortAccessor: (r) => dateSortValue(r.paid_at),
            render: (r) => <span className="text-xs text-foreground/80">{formatPaidDate(r.paid_at)}</span>,
        },
    ];

    return (
        <div className="space-y-6">
            <div className="flex items-start justify-between">
                <div>
                    <button
                        onClick={() => navigate(-1)}
                        className="text-sm text-shell-foreground/80 hover:text-shell-foreground inline-flex items-center gap-1 mb-2"
                    >
                        <ArrowLeft className="h-3 w-3" /> Back
                    </button>
                    <h1 className="text-2xl font-black tracking-tight text-shell-foreground">
                        Invoice <span className="font-mono">#{inv.invoice_number || inv.id.slice(0, 8)}</span>
                        {inv.title && <span className="font-normal text-shell-foreground/80"> — {inv.title}</span>}
                    </h1>
                    <div className="flex items-center gap-3 mt-2 flex-wrap">
                        <Badge className={statusColor(inv.status)} variant="outline">
                            {commercialDocStatusLabel(inv.status)}
                        </Badge>
                        <Badge variant="outline">Invoice</Badge>
                        <QboSyncBadge
                            row={qboSyncStatusQuery.data?.row ?? null}
                            onDrilldown={() => navigate("/money?tab=quickbooks")}
                            isAdmin={isAdmin}
                        />
                        {isAdmin && (
                            <QboCustomerPickButton
                                lastError={qboSyncStatusQuery.data?.row?.last_error}
                                invoiceId={inv.id}
                                onRetry={() => pushToQbo.mutate()}
                                retrying={pushToQbo.isPending}
                            />
                        )}
                        {inv.job_id && inv.source_job_title && (
                            <button
                                onClick={() => navigate(`/jobs/${inv.job_id}`)}
                                className="text-xs text-shell-foreground underline decoration-shell-foreground/45 underline-offset-2 hover:decoration-shell-foreground inline-flex items-center gap-1"
                            >
                                <Briefcase className="h-3 w-3" />
                                From Job: {inv.source_job_title}
                            </button>
                        )}
                        {inv.estimate_id && inv.source_estimate_title && (
                            <button
                                onClick={() => navigate(`/commercial?type=estimate&id=${inv.estimate_id}`)}
                                className="text-xs text-shell-foreground underline decoration-shell-foreground/45 underline-offset-2 hover:decoration-shell-foreground inline-flex items-center gap-1"
                            >
                                <Receipt className="h-3 w-3" />
                                From Estimate: {inv.source_estimate_title}
                            </button>
                        )}
                    </div>
                </div>
                <div className="flex gap-2">
                    <TaskCreateButton
                        prefill={{
                            title: `Invoice #${inv.invoice_number || inv.id.slice(0, 8)} — `,
                            links: [
                                {
                                    entityType: "invoice",
                                    entityId: inv.id,
                                    label: `Invoice #${inv.invoice_number || inv.id.slice(0, 8)}`,
                                },
                            ],
                        }}
                    />
                    {(inv.status === "draft" || inv.status === "open") && (
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() =>
                                setHeaderEdit({
                                    title: inv.title ?? "",
                                    notes: inv.notes ?? "",
                                    tax_rate: String(inv.tax_amount ?? "0"),
                                    payment_terms: "",
                                })
                            }
                        >
                            <Settings className="h-4 w-4 mr-1" />
                            Edit Details
                        </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => setPreviewOpen(true)}>
                        <Printer className="h-4 w-4 mr-1" />
                        Preview
                    </Button>
                    {/* Send / Resend: an invoice can be emailed again in ANY
                        status (Issue #510) — including paid (a receipt copy) and
                        void. The label reads "Send" only on a draft (first
                        issuance) and "Resend" once issued. The server switches a
                        paid/void email to receipt / "no payment due" language and
                        drops the pay link, so a paid customer never gets a
                        "please pay" email. Always shown. */}
                    <Button size="sm" onClick={() => setSendOpen(true)}>
                        <Send className="h-4 w-4 mr-1" />
                        {inv.status === "draft" ? "Send" : "Resend"}
                    </Button>
                    {["open", "sent", "partially_paid", "overdue"].includes(inv.status) && (
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() => copyPayLink.mutate()}
                            disabled={copyPayLink.isPending}
                        >
                            {copyPayLink.isPending ? (
                                <Loader2 className="h-4 w-4 animate-spin mr-1" />
                            ) : (
                                <Link2 className="h-4 w-4 mr-1" />
                            )}
                            Copy Pay Link
                        </Button>
                    )}
                    {inv.status !== "paid" && inv.status !== "void" && (
                        <Button
                            size="sm"
                            variant="outline"
                            data-testid="mark-paid-button"
                            className="border-emerald-300 text-emerald-800 hover:bg-emerald-50 dark:border-emerald-800 dark:text-emerald-300"
                            title="Records money already collected — cash, check, or card charged elsewhere. No card is charged here."
                            onClick={() => {
                                openMarkPaidForm();
                                setPaymentOpen(true);
                            }}
                        >
                            <Banknote className="h-4 w-4 mr-1" />
                            Mark Paid
                        </Button>
                    )}
                    {/* #1916 — one check, many jobs. */}
                    <PartOfBiggerCheckButton invoiceId={inv.id} customerId={inv.customer_id} status={inv.status} />
                    {(inv.status === "paid" || inv.status === "partially_paid") && canManagePayments && (
                        <Button
                            size="sm"
                            variant="outline"
                            data-testid="reopen-button"
                            onClick={async () => {
                                const ok = await confirmDialog({
                                    title: "Reopen this invoice?",
                                    message:
                                        "Removes the recorded (non-card) payments and reverts the invoice to open. A card charge must be reversed with Refund instead — Reopen will refuse if one is on file.",
                                    confirmLabel: "Reopen",
                                    destructive: true,
                                });
                                if (ok) reopenInvoice.mutate();
                            }}
                            disabled={reopenInvoice.isPending}
                        >
                            {reopenInvoice.isPending ? (
                                <Loader2 className="h-4 w-4 animate-spin mr-1" />
                            ) : (
                                <RotateCcw className="h-4 w-4 mr-1" />
                            )}
                            Reopen
                        </Button>
                    )}
                    {isAdmin && (
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() => pushToQbo.mutate()}
                            disabled={pushToQbo.isPending}
                        >
                            {pushToQbo.isPending ? (
                                <Loader2 className="h-4 w-4 animate-spin mr-1" />
                            ) : (
                                <Cloud className="h-4 w-4 mr-1" />
                            )}
                            Push to QBO
                        </Button>
                    )}
                    {canRefund && (
                        <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 hover:text-red-700 border-red-200 dark:border-red-900"
                            onClick={() => {
                                setRefundForm({
                                    amount: refundable.toFixed(2),
                                    reason: "requested_by_customer",
                                    note: "",
                                });
                                setRefundOpen(true);
                            }}
                        >
                            <Undo2 className="h-4 w-4 mr-1" />
                            Refund
                        </Button>
                    )}
                    {/* Void: the clean exit for an invoice that should never
                        have been billed (e.g. reads as "sent" but wasn't). Only
                        offered when nothing has been collected — the server
                        refuses a void with payments; use Reopen/Refund first.
                        Voiding also unblocks cancelling the job. */}
                    {!["paid", "void"].includes(inv.status) && totalPaid === 0 && (
                        <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 dark:text-red-400"
                            onClick={() => setVoidInvoiceOpen(true)}
                        >
                            <Ban className="h-4 w-4 mr-1" />
                            Void Invoice
                        </Button>
                    )}
                </div>
            </div>

            {/* Collect first: charge the saved card before (or instead of)
                sending — same panel as the Close Out review screen. */}
            <ChargeCardPanel
                invoice={{
                    id: inv.id,
                    invoiceNumber: inv.invoice_number || null,
                    status: inv.status,
                    total: Number(inv.total_amount ?? 0),
                    paid: totalPaid,
                    lastStripeRef: payments.find((p) => p.method === "stripe")?.reference ?? null,
                }}
                customer={{
                    customerId: inv.customer_id ?? null,
                    customerName: inv.customer_name ?? null,
                    hasCard: !!inv.has_card_on_file,
                    brand: inv.card_brand ?? null,
                    last4: inv.card_last4 ?? null,
                    paymentProfile: inv.payment_profile ?? null,
                }}
                onSettled={() => qc.invalidateQueries({ queryKey: ["invoice", id] })}
            />

            <div className="grid gap-4 md:grid-cols-3">
                <Card>
                    <CardHeader className="pb-3">
                        <CardTitle className="text-sm flex items-center gap-2">
                            <FileText className="h-4 w-4" /> Details
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm space-y-1">
                        {inv.invoice_number && (
                            <p>
                                <span className="text-foreground/80">Number:</span> {inv.invoice_number}
                            </p>
                        )}
                        <p>
                            <span className="text-foreground/80">Created:</span> {formatDate(inv.created_at)}
                        </p>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader className="pb-3">
                        <CardTitle className="text-sm flex items-center gap-2">
                            <DollarSign className="h-4 w-4" /> Total
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <p className="text-2xl font-bold">{formatDollars(Number(inv.total_amount))}</p>
                        <p className="text-xs text-foreground/80">Subtotal: {formatDollars(Number(inv.subtotal))}</p>
                        <TaxTreatmentPanel
                            docType="invoice"
                            docId={inv.id}
                            customerId={inv.customer_id ?? null}
                            doc={inv}
                            editable={inv.status === "draft"}
                            onSaved={() => qc.invalidateQueries({ queryKey: ["invoice", id] })}
                        />
                    </CardContent>
                </Card>
            </div>

            <div className="space-y-3">
                <div className="flex items-center justify-between">
                    <div>
                        <h2 className="text-lg font-semibold text-shell-foreground">Line Items</h2>
                        {linesNotItemized && (
                            <p className="text-xs text-shell-foreground/80 inline-flex items-center gap-1 mt-0.5">
                                <Cloud className="h-3 w-3" />
                                Imported from ServiceTitan, lines not itemized
                            </p>
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        {canEditInvLines && (
                            <Button
                                size="sm"
                                variant="outline"
                                disabled={!needsTidying(invoiceReorder.ordered) || invoiceReorder.saving}
                                title={
                                    needsTidying(invoiceReorder.ordered)
                                        ? "Group each device's lines together, labor above the materials"
                                        : "Already in order"
                                }
                                onClick={() =>
                                    invoiceReorder.apply(tidyLineOrder(invoiceReorder.ordered).map((l) => l.id))
                                }
                            >
                                <ArrowUpDown className="h-4 w-4 mr-1" />
                                Tidy up
                            </Button>
                        )}
                        {(inv.status === "draft" || inv.status === "open") && (
                            <PricebookLinePicker invoiceId={inv.id} />
                        )}
                    </div>
                </div>
                <DataTable
                    columns={lineCols}
                    data={invoiceReorder.ordered}
                    keyField="id"
                    rowProps={invoiceReorder.rowProps}
                    emptyMessage="No line items — click 'Add from pricebook' to drop in a billable item."
                />
                <BulkDeleteBar bulk={invoiceBulk} />
                {canCorrectLineText && (
                    <p className="text-xs text-foreground/60" data-testid="paid-invoice-text-only-note">
                        This invoice is paid, so the amounts are locked. You can still fix the wording — a mistyped
                        serial, or a test that says Incomplete after it passed. Correct the text, then Resend so the
                        customer has the right copy.
                    </p>
                )}
            </div>
            <div>
                <h2 className="text-lg font-semibold mb-3 text-shell-foreground">Payments</h2>
                <ReceivedPaymentTrace invoiceId={inv.id} />
                <DataTable
                    columns={paymentCols}
                    data={payments}
                    keyField="id"
                    sortId="commercial.invoice-payments"
                    emptyMessage="No payments recorded"
                />
            </div>

            {inv.notes && (
                <Card>
                    <CardHeader>
                        <CardTitle className="text-sm">Notes</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <p className="text-sm whitespace-pre-wrap">{inv.notes}</p>
                    </CardContent>
                </Card>
            )}

            <Dialog
                open={paymentOpen}
                onClose={() => setPaymentOpen(false)}
                title="Mark Paid — record collected payment"
            >
                <div className="space-y-4" data-testid="mark-paid-form">
                    <p className="text-xs text-foreground/70">
                        Records money already collected elsewhere (cash, check, or a card charged on another terminal).
                        This does <strong>not</strong> charge a card — use “Charge card” for that.
                    </p>
                    <div>
                        <Label>Payment Method</Label>
                        <Select
                            value={paymentForm.method}
                            onChange={(e) => setPaymentForm({ ...paymentForm, method: e.target.value })}
                        >
                            {["Cash", "Check", "Card — charged elsewhere", "ACH", "Zelle", "Other"].map((m) => (
                                <option key={m} value={m}>
                                    {m}
                                </option>
                            ))}
                        </Select>
                    </div>
                    <div>
                        <Label>
                            Reference <span className="text-foreground/80 font-normal">(optional)</span>
                        </Label>
                        <Input
                            placeholder="Check #, transaction ID, etc."
                            value={paymentForm.reference}
                            onChange={(e) => setPaymentForm({ ...paymentForm, reference: e.target.value })}
                        />
                    </div>
                    <div>
                        <Label>Date collected</Label>
                        <Input
                            type="date"
                            value={paymentForm.date}
                            onChange={(e) => setPaymentForm({ ...paymentForm, date: e.target.value })}
                        />
                    </div>
                    <div>
                        <Label>
                            Amount{" "}
                            <span className="text-foreground/80 font-normal">
                                (remaining balance {formatDollars(remainingBalance)})
                            </span>
                        </Label>
                        <Input
                            type="number"
                            step="0.01"
                            placeholder={remainingBalance.toFixed(2)}
                            value={paymentForm.amount}
                            onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                            aria-invalid={!!paymentAmountError}
                        />
                        {paymentAmountError ? (
                            <p className="mt-1 text-xs font-medium text-red-600 dark:text-red-400">
                                {paymentAmountError}
                            </p>
                        ) : (
                            <p className="mt-1 text-xs text-foreground/60">
                                Leave blank to record the full remaining balance.
                            </p>
                        )}
                    </div>
                </div>
                <div className="flex justify-end gap-2 mt-4">
                    <Button variant="outline" onClick={() => setPaymentOpen(false)}>
                        Cancel
                    </Button>
                    <Button
                        data-testid="mark-paid-submit"
                        onClick={() =>
                            recordPayment.mutate(
                                buildRecordPaymentBody({
                                    method: paymentForm.method,
                                    reference: paymentForm.reference,
                                    amount: paymentForm.amount,
                                    date: paymentForm.date,
                                }),
                            )
                        }
                        disabled={recordPayment.isPending || !!paymentAmountError}
                    >
                        {recordPayment.isPending ? (
                            <Loader2 className="h-4 w-4 animate-spin mr-1" />
                        ) : (
                            <Banknote className="h-4 w-4 mr-1" />
                        )}
                        Record Payment
                    </Button>
                </div>
            </Dialog>

            <Dialog open={refundOpen} onClose={() => setRefundOpen(false)} title="Refund Stripe Payment">
                <div className="space-y-4">
                    <p className="text-sm text-foreground/80">
                        Refunds the customer's card via Stripe and posts a matching RefundReceipt to QuickBooks. Up to{" "}
                        {formatDollars(refundable)} is refundable on this invoice.
                    </p>
                    <div>
                        <Label>
                            Amount{" "}
                            <span className="text-foreground/80 font-normal">(max {formatDollars(refundable)})</span>
                        </Label>
                        <Input
                            type="number"
                            step="0.01"
                            value={refundForm.amount}
                            onChange={(e) => setRefundForm({ ...refundForm, amount: e.target.value })}
                        />
                    </div>
                    <div>
                        <Label>Reason</Label>
                        <Select
                            value={refundForm.reason}
                            onChange={(e) => setRefundForm({ ...refundForm, reason: e.target.value })}
                        >
                            <option value="requested_by_customer">Requested by customer</option>
                            <option value="duplicate">Duplicate</option>
                            <option value="fraudulent">Fraudulent</option>
                        </Select>
                    </div>
                    <div>
                        <Label>
                            Note <span className="text-foreground/80 font-normal">(optional)</span>
                        </Label>
                        <Textarea
                            rows={2}
                            placeholder="Internal note / memo on the QBO RefundReceipt"
                            value={refundForm.note}
                            onChange={(e) => setRefundForm({ ...refundForm, note: e.target.value })}
                        />
                    </div>
                </div>
                <div className="flex justify-end gap-2 mt-4">
                    <Button variant="outline" onClick={() => setRefundOpen(false)}>
                        Cancel
                    </Button>
                    <Button
                        className="bg-red-600 hover:bg-red-700 text-white"
                        onClick={() => {
                            const amt = Number(refundForm.amount);
                            if (!Number.isFinite(amt) || amt <= 0) {
                                toast.error("Enter a refund amount greater than 0");
                                return;
                            }
                            if (amt > refundable + 0.001) {
                                toast.error(`Refund cannot exceed ${formatDollars(refundable)}`);
                                return;
                            }
                            refund.mutate({
                                amountCents: Math.round(amt * 100),
                                reason: refundForm.reason,
                                note: refundForm.note.trim() || undefined,
                            });
                        }}
                        disabled={refund.isPending}
                    >
                        {refund.isPending ? (
                            <Loader2 className="h-4 w-4 animate-spin mr-1" />
                        ) : (
                            <Undo2 className="h-4 w-4 mr-1" />
                        )}
                        Issue Refund
                    </Button>
                </div>
            </Dialog>

            {headerEdit && (
                <Dialog open={true} onClose={() => setHeaderEdit(null)} title="Edit Invoice Details">
                    <div className="space-y-3">
                        <div>
                            <Label>Title</Label>
                            <Input
                                value={headerEdit.title}
                                onChange={(e) => setHeaderEdit({ ...headerEdit, title: e.target.value })}
                            />
                        </div>
                        <div>
                            <Label>Tax Amount</Label>
                            <Input
                                type="number"
                                step="0.01"
                                value={headerEdit.tax_rate}
                                onChange={(e) => setHeaderEdit({ ...headerEdit, tax_rate: e.target.value })}
                            />
                        </div>
                        <div>
                            <Label>Notes</Label>
                            <Textarea
                                rows={3}
                                value={headerEdit.notes}
                                onChange={(e) => setHeaderEdit({ ...headerEdit, notes: e.target.value })}
                            />
                        </div>
                    </div>
                    <div className="flex justify-end gap-2 mt-4">
                        <Button variant="outline" onClick={() => setHeaderEdit(null)}>
                            Cancel
                        </Button>
                        <Button
                            onClick={() =>
                                saveInvoiceHeader.mutate({
                                    title: headerEdit.title,
                                    notes: headerEdit.notes,
                                    taxAmount: Number(headerEdit.tax_rate) || 0,
                                })
                            }
                            disabled={saveInvoiceHeader.isPending}
                        >
                            {saveInvoiceHeader.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}Save
                        </Button>
                    </div>
                </Dialog>
            )}

            <DocumentPreviewDialog
                open={previewOpen}
                onClose={() => setPreviewOpen(false)}
                kind="invoice"
                documentId={id ?? null}
                label={inv.invoice_number ?? inv.title ?? null}
                suggestedFilename={inv.invoice_number ? `invoice-${inv.invoice_number}.pdf` : null}
            />

            {id && (
                <SendCustomerDialog
                    docType="invoice"
                    id={id}
                    customerId={inv.customer_id ?? null}
                    open={sendOpen}
                    onOpenChange={setSendOpen}
                    onSent={() => qc.invalidateQueries({ queryKey: ["invoice", id] })}
                />
            )}

            <VoidInvoiceDialog
                open={voidInvoiceOpen}
                onClose={() => setVoidInvoiceOpen(false)}
                invoiceId={inv.id}
                invoiceLabel={`Invoice #${inv.invoice_number || inv.id.slice(0, 8)}`}
            />
        </div>
    );
}

// ─── Originating-job summary card ─────────────────────────────────────
//
// Per PRE-CUTOVER-UX-FEEDBACK.md #32 every estimate should carry a
// "View Job" button + a summary of the job it was generated from.
// We render it as a third tile in the top stat row (next to Details
/**
 * The estimate's title + scope of work, read and edited in place.
 *
 * These are the two fields the office rewrites on nearly every quote, and both
 * are customer-facing: the title heads the PDF and the email subject, and
 * `notes` renders as the **SCOPE OF WORK** block on the estimate PDF
 * (`pdfBuilders/estimatePdf.ts`). Before this they were the hardest things on
 * the page to work with — the title showed only in the page header, and the
 * scope text rendered in a card BELOW the line items and the deposit panel,
 * and not at all when it was empty. So a blank scope could only be filled from
 * the Edit Details modal, and a written one had to be scrolled past everything
 * to be read.
 *
 * Empty state matters as much as the filled one: an estimate with no scope
 * still shows the card, saying so, with the way to write it — the customer's
 * "why" is not an optional field we hide when it's missing.
 */
export function CustomerFacingTextCard({
    title,
    notes,
    editable,
    saving,
    onSave,
}: {
    title: string;
    notes: string;
    editable: boolean;
    saving: boolean;
    onSave: (next: { title: string; notes: string }) => void;
}) {
    const [draft, setDraft] = useState<{ title: string; notes: string } | null>(null);
    const editing = draft !== null;

    if (!editing) {
        return (
            <Card>
                <CardHeader className="pb-3 flex-row items-center justify-between space-y-0">
                    <CardTitle className="text-sm flex items-center gap-2">
                        <FileText className="h-4 w-4" /> What the customer sees
                    </CardTitle>
                    {editable && (
                        <Button size="sm" variant="outline" onClick={() => setDraft({ title, notes })}>
                            <Settings className="h-4 w-4 mr-1" />
                            Edit
                        </Button>
                    )}
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                    <div>
                        <div className="text-[10px] uppercase tracking-wide text-foreground/70">Title</div>
                        {title ? (
                            <p className="font-medium">{title}</p>
                        ) : (
                            <p className="text-muted-foreground">No title yet.</p>
                        )}
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-wide text-foreground/70">
                            Scope of work — the &ldquo;why&rdquo; on the customer&apos;s quote
                        </div>
                        {notes ? (
                            <p className="whitespace-pre-wrap">{notes}</p>
                        ) : (
                            <p className="text-muted-foreground">
                                Nothing written yet — the quote goes out with no explanation of the work.
                            </p>
                        )}
                    </div>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card>
            <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                    <FileText className="h-4 w-4" /> What the customer sees
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
                <div>
                    <Label>Title</Label>
                    <Input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
                </div>
                <div>
                    <Label>Scope of work</Label>
                    <Textarea
                        rows={5}
                        value={draft.notes}
                        onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                        placeholder="Why this work is needed, in the customer's language. This prints as SCOPE OF WORK on their quote."
                    />
                </div>
                <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setDraft(null)} disabled={saving}>
                        Cancel
                    </Button>
                    <Button
                        onClick={() => {
                            onSave({ title: draft.title, notes: draft.notes });
                            setDraft(null);
                        }}
                        disabled={saving}
                    >
                        {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                        Save
                    </Button>
                </div>
            </CardContent>
        </Card>
    );
}

// and Subtotal) so the office can jump back to the test/repair job
// the estimate is billing for without leaving the estimate.
//
// Fields surfaced (per spec): job number, status, scheduled date,
// tech name. Missing fields are omitted gracefully so older jobs
// without scheduling or assignment still render usefully.
function OriginatingJobSummaryCard({
    jobId,
    title,
    displayNumber,
    status,
    scheduledStart,
    techName,
    onNavigate,
}: {
    jobId: string;
    title: string | null;
    displayNumber: string | null;
    status: string | null;
    scheduledStart: string | null;
    techName: string | null;
    onNavigate: () => void;
}) {
    void jobId;
    const formattedDate = scheduledStart ? formatDate(scheduledStart) : null;
    return (
        <Card>
            <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                    <Briefcase className="h-4 w-4" /> Originating Job
                </CardTitle>
            </CardHeader>
            <CardContent className="text-sm space-y-2">
                <div className="flex items-center gap-2 flex-wrap">
                    {displayNumber ? <span className="font-mono text-xs font-semibold">#{displayNumber}</span> : null}
                    {status ? (
                        <Badge className={statusColor(status)} variant="outline">
                            {commercialDocStatusLabel(status)}
                        </Badge>
                    ) : null}
                </div>
                {title ? <p className="font-medium leading-snug">{title}</p> : null}
                <div className="text-xs text-foreground/80 space-y-0.5">
                    {formattedDate ? (
                        <p>
                            <span className="text-foreground/60">Scheduled:</span> {formattedDate}
                        </p>
                    ) : (
                        <p className="italic text-foreground/60">Not scheduled</p>
                    )}
                    <p>
                        <span className="text-foreground/60">Tech:</span>{" "}
                        {techName?.trim() ? techName : <span className="italic text-foreground/60">Unassigned</span>}
                    </p>
                </div>
                <Button size="sm" variant="outline" className="w-full mt-1" onClick={onNavigate}>
                    <ExternalLink className="h-3.5 w-3.5 mr-1.5" /> View Job
                </Button>
            </CardContent>
        </Card>
    );
}
